import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  collection,
  onSnapshot,
  doc,
  setDoc,
  addDoc,
  deleteDoc,
  query,
  serverTimestamp
} from 'firebase/firestore';
import { db, appId } from './firebase-config';
import {
  resolveTransportsCollectionPath,
  resolveDriversCollectionPath,
  resolveCompanyLiquidationSettingsPath,
  resolveFleetExpensesCollectionPath,
  resolveScanEventsCollectionPath,
  resolveServiceContractsCollectionPath
} from './core/company/companyPaths';
import { Icon, calculateRouteCost, getRouteStatus } from './modules/logistics/components/LogisticsUtils';
import { resolveTransportIdentity, withAlpha, mixHexColor } from './modules/logistics/components/transportIdentity';
import {
  buildDriverDisplayName,
  buildDriverSnapshot,
  buildTransportDisplayName,
  buildTransportMeta,
  buildOperationalAccessUrl
} from './modules/logistics/components/driverUtils';
import { getRouteTotalDistance } from './modules/reports/utils/routeMonitorUtils';
import { resolveCompanyPlanMeta } from './companyPlans';
import {
  getProfilePhotoUploadErrorMessage,
  uploadCompressedProfilePhoto
} from './core/media/profilePhotoUpload';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { DRIVERS_BOARD_TUTORIAL_STEPS } from './modules/logistics/tutorials/driversBoardTutorial';

const DEFAULT_COMPANY_ID = 'default';
const DRIVERS_BOARD_TUTORIAL_STORAGE_KEY = 'gcentra_tutorial_drivers_board_seen';
const DRIVERS_TUTORIAL_PANEL_WIDTH = 288;
const DRIVERS_TUTORIAL_PANEL_HEIGHT = 420;
const IDENTITY_COLOR_OPTIONS = [
  '#2563eb',
  '#0ea5e9',
  '#06b6d4',
  '#10b981',
  '#84cc16',
  '#f59e0b',
  '#f97316',
  '#ef4444',
  '#ec4899',
  '#8b5cf6',
  '#6366f1',
  '#14b8a6'
];

const TRANSPORT_TYPE_OPTIONS = [
  { value: 'camioneta', label: 'Camioneta' },
  { value: 'moto', label: 'Moto' },
  { value: 'auto', label: 'Auto' },
  { value: 'camion', label: 'Camión' }
];

const TRANSPORT_STATUS_OPTIONS = [
  { value: 'available', label: 'Disponible' },
  { value: 'assigned', label: 'Asignada' },
  { value: 'parking', label: 'En depósito' },
  { value: 'in_route', label: 'En ruta' },
  { value: 'service', label: 'Fuera de servicio' },
  { value: 'inactive', label: 'Inactiva' }
];

const isRouteOperationallyActive = (route = {}) => {
  if (!route || typeof route !== 'object') return false;
  const normalizedStatus = getRouteStatus(route);
  return !['completed', 'cancelled'].includes(normalizedStatus);
};

// Helpers
const slugify = (text = '') =>
  text
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const generateRandomPassword = () => {
  return Math.random().toString(36).slice(-6).toUpperCase();
};

const formatCurrency = (value) => {
  const parsed = Number(value || 0);
  return parsed.toLocaleString('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

const formatShortDate = (value) => {
  if (!value) return 'Sin registro';
  if (value?.toDate) {
    return value.toDate().toLocaleDateString('es-AR');
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Sin registro';
  return parsed.toLocaleDateString('es-AR');
};

const getEventTimestampMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const formatActionDateTime = (value) => {
  const millis = getEventTimestampMillis(value);
  if (!millis) return 'Sin hora';
  return new Date(millis).toLocaleString('es-AR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const buildTraceActionMeta = (event = {}) => {
  const status = String(event?.resultStatus || '').trim().toLowerCase();
  const evidenceType = String(event?.evidenceType || '').trim().toLowerCase();
  const source = String(event?.source || '').trim().toLowerCase();
  const scannedValue = String(event?.scannedValue || event?.rawValue || '').trim();
  const targetLabel = String(event?.targetLabel || event?.destinationName || '').trim();

  if (status === 'evidence_uploaded' || evidenceType === 'photo' || source === 'photo_upload') {
    return {
      tone: 'cyan',
      badge: 'Foto',
      title: 'Subió evidencia del destino',
      detail: targetLabel || 'Se registró una foto operativa en la ruta.',
      iconPath: 'M3.375 6.375A2.625 2.625 0 016 3.75h12a2.625 2.625 0 012.625 2.625v11.25A2.625 2.625 0 0118 20.25H6a2.625 2.625 0 01-2.625-2.625V6.375zm11.25 1.875a1.125 1.125 0 100-2.25 1.125 1.125 0 000 2.25zm-8.25 8.625h11.25l-3.11-4.148a.75.75 0 00-1.18-.03l-2.22 2.664-1.47-1.47a.75.75 0 00-1.138.086L6.375 16.875z'
    };
  }

  if (status === 'found') {
    return {
      tone: 'emerald',
      badge: source === 'camera_barcode_detector' ? 'QR / Barra' : 'Escaneo',
      title: 'Validó un código correctamente',
      detail: scannedValue || targetLabel || 'El escaneo coincidió con la operación.',
      iconPath: 'M3.75 4.5h3v3h-3v-3zm0 12h3v3h-3v-3zm12-12h3v3h-3v-3zm0 6h3v3h-3v-3zm-6 0h3v3h-3v-3zm0 6h3v3h-3v-3z'
    };
  }

  if (status === 'multiple') {
    return {
      tone: 'amber',
      badge: 'Escaneo',
      title: 'Encontró varias coincidencias',
      detail: scannedValue || 'Conviene revisar la validación manualmente.',
      iconPath: 'M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 6h.008v.008H12v-.008z'
    };
  }

  if (status === 'not_found') {
    return {
      tone: 'rose',
      badge: 'Escaneo',
      title: 'Registró un código sin coincidencia',
      detail: scannedValue || 'El evento quedó guardado para seguimiento.',
      iconPath: 'M12 9v3.75m0 3.75h.008v.008H12v-.008zm9-3.758a9 9 0 11-18 0 9 9 0 0118 0z'
    };
  }

  return {
    tone: 'slate',
    badge: 'Evento',
    title: 'Acción operativa registrada',
    detail: scannedValue || targetLabel || 'Quedó un movimiento en la trazabilidad.',
    iconPath: 'M11.25 6.75h7.5m-7.5 4.5h7.5m-7.5 4.5h4.5m-9-9h.008v.008H6.75V6.75zm0 4.5h.008v.008H6.75v-.008zm0 4.5h.008v.008H6.75v-.008z'
  };
};

const buildRecentActionPreview = (event = {}) => {
  const meta = buildTraceActionMeta(event);
  return {
    ...meta,
    timeLabel: formatActionDateTime(event.createdAt),
    destinationLabel: String(event?.destinationName || event?.targetLabel || '').trim(),
    routeLabel: String(
      event?.transportNameSnapshot ||
      event?.driverNameSnapshot ||
      event?._routeLabel ||
      ''
    ).trim(),
  };
};


const DRIVER_STATUS_TONES = Object.freeze({
  en_ruta: {
    label: 'En ruta',
    pill: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    accent: 'from-emerald-500/16 to-teal-500/12',
    dot: 'bg-emerald-500'
  },
  asignado: {
    label: 'Asignado',
    pill: 'bg-sky-100 text-sky-700 border-sky-200',
    accent: 'from-sky-500/16 to-blue-500/12',
    dot: 'bg-sky-500'
  },
  disponible: {
    label: 'Disponible',
    pill: 'bg-amber-100 text-amber-700 border-amber-200',
    accent: 'from-amber-500/16 to-orange-500/12',
    dot: 'bg-amber-500'
  },
  inactivo: {
    label: 'Inactivo',
    pill: 'bg-slate-200 text-slate-700 border-slate-300',
    accent: 'from-slate-400/10 to-slate-500/8',
    dot: 'bg-slate-400'
  }
});

const DriverAvatar = ({ photoURL = '', displayName = 'Chofer', sizeClass = 'h-14 w-14' }) => {
  const initials = String(displayName || 'Chofer')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'CH';

  if (photoURL) {
    return (
      <div className={`driver-photo-shell ${sizeClass} shrink-0 rounded-[20px] ring-1 ring-white/70 shadow-[0_10px_24px_rgba(15,23,42,0.12)]`}>
        <img
          src={photoURL}
          alt={displayName}
          className="driver-photo-img h-full w-full object-cover"
        />
        <span className="driver-photo-glow" />
      </div>
    );
  }

  return (
    <div className={`${sizeClass} shrink-0 rounded-[20px] bg-[linear-gradient(135deg,#0f172a_0%,#1d4ed8_100%)] text-white ring-1 ring-white/30 shadow-[0_10px_24px_rgba(15,23,42,0.14)] flex items-center justify-center`}>
      <span className="text-sm font-black tracking-[0.18em]">{initials}</span>
    </div>
  );
};


const DriversCommandBoard = ({
  sectionRef,
  drivers = [],
  driverCards = [],
  selectedDriverId = '',
  selectedDriverReport = null,
  expandedDriverCardId = '',
  onSelectDriver,
  onToggleDriverCard,
  onOpenTransport,
  onOpenRouteSheet,
  onOpenRouteMonitor,
  onCopyAccess,
  onOpenNewRecord,
  onClose
}) => {
  const [showRouteHistory, setShowRouteHistory] = useState(false);
  const [tutorialSeen, setTutorialSeen] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(DRIVERS_BOARD_TUTORIAL_STORAGE_KEY) === 'true';
  });
  const [tutorialRun, setTutorialRun] = useState(false);
  const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
  const [tutorialShortcutOpen, setTutorialShortcutOpen] = useState(false);
  const [tutorialSessionKey, setTutorialSessionKey] = useState(0);
  const tutorialShortcutPanelRef = useRef(null);
  const tutorialJumpTimeoutsRef = useRef([]);
  const tutorialSteps = useMemo(() => DRIVERS_BOARD_TUTORIAL_STEPS, []);
  const currentTutorialTarget = tutorialRun ? tutorialSteps[tutorialStepIndex]?.target || null : null;

  useEffect(() => {
    setShowRouteHistory(false);
  }, [selectedDriverId]);

  const clearTutorialJumpTimeouts = useCallback(() => {
    tutorialJumpTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    tutorialJumpTimeoutsRef.current = [];
  }, []);

  const applyTutorialStepSideEffects = useCallback((stepIndex) => {
    const step = tutorialSteps[stepIndex];
    const target = step?.target || '';

    if (target === '#drivers-board-route-history') {
      setShowRouteHistory(true);
      return;
    }

    if (target === '#drivers-board-recent-history') {
      setShowRouteHistory(false);
    }
  }, [tutorialSteps]);

  const scrollToTutorialTarget = useCallback((targetSelector, options = {}) => {
    if (typeof window === 'undefined' || !targetSelector) return;

    const { attempts = 16, delay = 120 } = options;
    clearTutorialJumpTimeouts();

    const centerNodeInViewport = (node) => {
      const rect = node.getBoundingClientRect();
      const absoluteTop = rect.top + window.scrollY;
      const viewportOffset = Math.max(96, (window.innerHeight - rect.height) / 2);
      const nextTop = Math.max(0, absoluteTop - viewportOffset);
      window.scrollTo({ top: nextTop, behavior: 'smooth' });

      const scrollParent = node.closest('[data-tutorial-scroll-container], [data-drivers-scroll], .overflow-y-auto');
      if (scrollParent && scrollParent.scrollTo) {
        const parentRect = scrollParent.getBoundingClientRect();
        const parentTop = scrollParent.scrollTop + (rect.top - parentRect.top);
        const parentOffset = Math.max(24, (parentRect.height - rect.height) / 2);
        scrollParent.scrollTo({ top: Math.max(0, parentTop - parentOffset), behavior: 'smooth' });
      }
    };

    const run = (remaining) => {
      const targetNode = document.querySelector(targetSelector);
      if (targetNode) {
        centerNodeInViewport(targetNode);
        return;
      }

      if (remaining <= 0) return;
      const timeoutId = window.setTimeout(() => run(remaining - 1), delay);
      tutorialJumpTimeoutsRef.current.push(timeoutId);
    };

    run(attempts);
  }, [clearTutorialJumpTimeouts]);

  useEffect(() => {
    if (!tutorialRun || !currentTutorialTarget) return undefined;

    applyTutorialStepSideEffects(tutorialStepIndex);
    const timeoutId = window.setTimeout(() => {
      scrollToTutorialTarget(currentTutorialTarget);
    }, 90);

    return () => window.clearTimeout(timeoutId);
  }, [applyTutorialStepSideEffects, currentTutorialTarget, scrollToTutorialTarget, tutorialRun, tutorialStepIndex]);

  useEffect(() => () => {
    clearTutorialJumpTimeouts();
  }, [clearTutorialJumpTimeouts]);

  const finishTutorialRun = useCallback(() => {
    clearTutorialJumpTimeouts();
    setTutorialRun(false);
    setTutorialStepIndex(0);
    setTutorialShortcutOpen(false);
    setShowRouteHistory(false);
    setTutorialSessionKey((current) => current + 1);
  }, [clearTutorialJumpTimeouts]);

  const startTutorial = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(DRIVERS_BOARD_TUTORIAL_STORAGE_KEY, 'true');
    }
    clearTutorialJumpTimeouts();
    setTutorialSeen(true);
    setTutorialRun(true);
    setTutorialStepIndex(0);
    setTutorialShortcutOpen(true);
    setShowRouteHistory(false);
  }, [clearTutorialJumpTimeouts]);

  const jumpToTutorialStep = useCallback((targetIndex) => {
    const nextStep = tutorialSteps[targetIndex];
    if (!nextStep) return;
    applyTutorialStepSideEffects(targetIndex);
    setTutorialStepIndex(targetIndex);
    window.setTimeout(() => {
      scrollToTutorialTarget(nextStep.target);
    }, 90);
  }, [applyTutorialStepSideEffects, scrollToTutorialTarget, tutorialSteps]);

  const handleTutorialCallback = useCallback((data) => {
    const { action, index, status, type } = data;

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status) || action === ACTIONS.CLOSE) {
      finishTutorialRun();
      return;
    }

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      if (nextIndex >= 0 && nextIndex < tutorialSteps.length) {
        applyTutorialStepSideEffects(nextIndex);
        setTutorialStepIndex(nextIndex);
        return;
      }

      if (nextIndex >= tutorialSteps.length || (index >= tutorialSteps.length - 1 && action !== ACTIONS.PREV)) {
        finishTutorialRun();
      }
    }
  }, [applyTutorialStepSideEffects, finishTutorialRun, tutorialSteps.length]);

  const selectedReport = selectedDriverReport;
  const transportMetaLabel = selectedReport?.currentTransportMeta || selectedReport?.currentTransportName || 'Sin unidad asignada';

  return (
    <>
      <Joyride
        key={`drivers-board-${tutorialSessionKey}`}
        run={tutorialRun}
        stepIndex={tutorialStepIndex}
        steps={tutorialSteps}
        callback={handleTutorialCallback}
        continuous
        disableScrolling
        hideCloseButton={false}
        showProgress
        showSkipButton
        disableOverlayClose
        spotlightClicks
        scrollOffset={96}
        styles={{
          options: {
            primaryColor: '#06b6d4',
            zIndex: 1400,
            arrowColor: '#ffffff',
            backgroundColor: '#ffffff',
            textColor: '#0f172a',
            overlayColor: 'rgba(15, 23, 42, 0.28)',
          },
          tooltip: {
            borderRadius: 20,
            boxShadow: '0 24px 54px rgba(15,23,42,0.18)',
            padding: '18px 18px 14px',
            textAlign: 'left',
          },
          buttonNext: {
            backgroundColor: '#0891b2',
            borderRadius: 999,
            fontWeight: 700,
          },
          buttonBack: {
            color: '#0f172a',
            marginRight: 8,
          },
          buttonSkip: {
            color: '#64748b',
            fontWeight: 700,
          },
        }}
        locale={{
          back: 'Atrás',
          close: 'Cerrar',
          last: 'Finalizar',
          next: 'Siguiente',
          open: 'Abrir',
          skip: 'Omitir',
        }}
      />

      <section
        id="logistics-drivers-board"
        ref={sectionRef}
        className="relative overflow-hidden rounded-[34px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#ecfeff_48%,#eff6ff_100%)] shadow-[0_26px_60px_rgba(15,23,42,0.10)]"
      >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.10),transparent_24%),radial-gradient(circle_at_bottom_right,rgba(14,116,144,0.10),transparent_24%)]" />
      <div className="relative z-10 space-y-5 p-4 md:p-5 xl:p-6">
        <div id="drivers-board-header" className="flex flex-col gap-3 rounded-[28px] border border-white/80 bg-white/72 px-4 py-3 shadow-[0_16px_34px_rgba(15,23,42,0.06)] lg:flex-row lg:items-center lg:justify-between">
          <div className="relative z-10 min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-800 bg-[linear-gradient(180deg,#1e293b,#0f172a)] px-4 py-2 text-sm font-bold text-white shadow-[0_16px_30px_rgba(15,23,42,0.18)] transition hover:-translate-y-[1px] hover:bg-slate-800"
              >
                <Icon path="M9 15L3 9m0 0l6-6M3 12h12a6 6 0 010 12h-3" className="h-4 w-4" />
                <span>Volver a logística</span>
              </button>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2.5">
                  <h2 id="drivers-board-intro" className="text-[28px] font-black tracking-[-0.04em] text-slate-900">Choferes</h2>
                  <span className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-sky-700">
                    <span className="inline-flex h-2.5 w-2.5 rounded-full bg-sky-500" />
                    {drivers.length} en base
                  </span>
                </div>
                <p className="mt-1 text-sm font-medium text-slate-500">Estado e informes</p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={tutorialRun ? finishTutorialRun : startTutorial}
              className={`inline-flex items-center gap-2 rounded-2xl border border-cyan-300 bg-cyan-50 px-4 py-2 text-sm font-semibold text-cyan-800 transition-all hover:-translate-y-0.5 hover:bg-cyan-100 ${
                tutorialRun || !tutorialSeen
                  ? 'shadow-[0_0_0_6px_rgba(34,211,238,0.14),0_0_24px_rgba(34,211,238,0.28)]'
                  : ''
              }`}
            >
              <Icon path="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" className="h-4 w-4" />
              {tutorialRun ? 'Cerrar tutorial' : 'Tutorial'}
            </button>
            <button
              type="button"
              onClick={onOpenNewRecord}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-[0_14px_30px_rgba(15,23,42,0.08)] transition hover:-translate-y-0.5 hover:border-slate-300 hover:bg-slate-50"
            >
              <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
              Nuevo registro
            </button>
          </div>
        </div>

        {drivers.length === 0 ? (
          <div className="rounded-[28px] border border-dashed border-sky-200 bg-white/80 px-6 py-8 text-center shadow-[0_18px_40px_rgba(15,23,42,0.06)]">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[24px] bg-sky-50 text-sky-600 shadow-[0_16px_30px_rgba(14,165,233,0.18)]">
              <Icon path="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.91m-7.5-2.482L15 19.128m0 0l-2.625-.372m2.625.372a9.337 9.337 0 01-4.121-.91m4.121.91a9.34 9.34 0 01-4.121-.91m0 0A9.373 9.373 0 015.25 15.75a9.373 9.373 0 01-1.34-4.992 9.337 9.337 0 01.91-4.121m0 0A9.373 9.373 0 017.5 4.25a9.373 9.373 0 014.992-1.34 9.337 9.337 0 014.121.91m0 0a9.373 9.373 0 012.625 2.625m-12.15 12.15A9.373 9.373 0 014.25 15.75m0 0a9.373 9.373 0 01-1.34-4.992" className="h-8 w-8" />
            </div>
            <h3 className="mt-4 text-lg font-bold text-slate-900">Todavía no hay choferes cargados</h3>
            <p className="mt-2 text-sm text-slate-500">
              Abrí un nuevo registro operativo para dar de alta el primer chofer y empezar a leer la base operativa desde esta vista.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[360px,minmax(0,1fr)] xl:items-start">
            <div id="drivers-board-list" data-drivers-scroll="true" className="max-h-[calc(100vh-17rem)] space-y-2.5 overflow-y-auto pr-2 [scrollbar-color:#38bdf8_#dbeafe] [scrollbar-width:thin]">
              {driverCards.map((driverCard) => {
                const isActive = selectedDriverId === driverCard.id;
                const isExpanded = expandedDriverCardId === driverCard.id;
                const statusTone = DRIVER_STATUS_TONES[driverCard.statusTone] || DRIVER_STATUS_TONES.disponible;

                return (
                  <article
                    key={driverCard.id}
                    id={driverCard === driverCards[0] ? 'drivers-board-first-card' : undefined}
                    className={`group overflow-hidden rounded-[24px] border transition-all duration-300 ${
                      isActive
                        ? 'border-cyan-200 bg-[linear-gradient(180deg,#ffffff_0%,#e0f2fe_100%)] shadow-[0_22px_48px_rgba(14,165,233,0.18),0_0_0_1px_rgba(56,189,248,0.24),0_0_24px_rgba(34,211,238,0.12)]'
                        : 'border-slate-200/80 bg-white/92 shadow-[0_12px_28px_rgba(15,23,42,0.08)] hover:border-slate-300 hover:shadow-[0_18px_34px_rgba(15,23,42,0.10)]'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => onToggleDriverCard?.(driverCard.id)}
                      className={`relative flex w-full items-center gap-3 overflow-hidden px-4 py-3 text-left transition-all duration-300 ${
                        isActive ? 'bg-[linear-gradient(135deg,rgba(224,242,254,0.88)_0%,rgba(255,255,255,0.96)_72%)]' : ''
                      }`}
                    >
                      <span className={`pointer-events-none absolute inset-0 ${isActive ? 'bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.24),transparent_34%)] opacity-90' : 'bg-[linear-gradient(120deg,rgba(255,255,255,0.08)_0%,transparent_38%,rgba(255,255,255,0.02)_100%)]'}`} />
                      <span
                        className={`pointer-events-none absolute inset-y-0 left-[-24%] w-[34%] ${isActive ? 'opacity-90' : 'opacity-45'}`}
                        style={{ background: isActive ? 'linear-gradient(90deg,transparent 0%,rgba(255,255,255,0.05) 18%,rgba(255,255,255,0.72) 50%,rgba(255,255,255,0.06) 82%,transparent 100%)' : 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.22) 48%, transparent 100%)', transform: 'skewX(-18deg)', animation: isActive ? 'driversCardShine 5.8s linear infinite' : 'moduleShine 10.6s ease-in-out infinite', filter: isActive ? 'blur(1px)' : 'none' }}
                      />
                    <div className="relative z-10"><DriverAvatar photoURL={driverCard.photoURL} displayName={driverCard.displayName} sizeClass="h-12 w-12" /></div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[15px] font-black tracking-[-0.03em] text-slate-900">{driverCard.displayName}</p>
                      </div>
                      <div className="relative z-10 flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${statusTone.pill}`}>
                          <span className={`inline-flex h-2 w-2 rounded-full ${statusTone.dot}`} />
                          {driverCard.statusLabel}
                        </span>
                        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                          <Icon path="M19.5 8.25l-7.5 7.5-7.5-7.5" className="h-4 w-4" />
                        </span>
                      </div>
                    </button>

                    <div className={`overflow-hidden border-t border-slate-200/80 bg-white/86 px-4 transition-all duration-300 ${isExpanded ? 'max-h-40 py-3 opacity-100' : 'max-h-0 py-0 opacity-0'}`}>
                      <div className="grid grid-cols-1 gap-2 text-[11px] sm:grid-cols-2">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
                          <p className="font-semibold uppercase tracking-[0.12em] text-slate-400">Acceso</p>
                          <p className="mt-1 truncate font-bold text-slate-800">{driverCard.accessSummary}</p>
                          <p className="mt-1 break-all font-mono text-[11px] font-semibold text-slate-600">{driverCard.accessPasswordSummary}</p>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
                          <p className="font-semibold uppercase tracking-[0.12em] text-slate-400">Unidad actual</p>
                          <p className="mt-1 truncate font-bold text-slate-800">{driverCard.currentTransportName}</p>
                          <p className="truncate text-slate-500">{driverCard.currentTransportMeta}</p>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="xl:sticky xl:top-4 xl:self-start">
              {selectedReport ? (
                <div className="space-y-4 rounded-[28px] border border-slate-200 bg-white/90 p-4 shadow-[0_20px_48px_rgba(15,23,42,0.08)] backdrop-blur-sm">
                  <div id="drivers-board-report-header" className="relative overflow-hidden flex flex-col gap-4 rounded-[24px] border border-cyan-200/60 bg-[linear-gradient(135deg,#0f172a_0%,#155e75_44%,#0f766e_100%)] p-4 text-white shadow-[0_18px_38px_rgba(8,47,73,0.22),0_0_0_1px_rgba(34,211,238,0.18),0_0_32px_rgba(34,211,238,0.12),inset_0_1px_0_rgba(255,255,255,0.14)] md:flex-row md:items-center md:justify-between">
                    <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.08)_0%,transparent_34%,rgba(255,255,255,0.04)_100%)]" />
                    <span className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 animate-[moduleShine_5.2s_ease-in-out_infinite]" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.40) 45%, transparent 100%)', transform: 'skewX(-18deg)' }} />
                    <div className="relative z-10 min-w-0 flex items-center gap-4">
                      <DriverAvatar photoURL={selectedReport.photoURL} displayName={selectedReport.displayName} sizeClass="h-20 w-20" />
                      <div className="min-w-0">
                        <p className="truncate text-[26px] font-black tracking-[-0.04em]">{selectedReport.displayName}</p>
                        <p className="mt-1 text-sm text-cyan-100">{selectedReport.currentTransportName} · {transportMetaLabel}</p>
                        <p className="mt-2 text-xs uppercase tracking-[0.18em] text-cyan-100/80">
                          Acceso {selectedReport.accessPath ? 'activo' : 'pendiente'} · Alta {selectedReport.firstSeenLabel}
                        </p>
                        <p className="mt-1 break-all font-mono text-[12px] font-semibold text-cyan-50/90">
                          {selectedReport.accessPassword ? `Clave ${selectedReport.accessPassword}` : 'Clave pendiente'}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
                      <button
                        type="button"
                        onClick={() => onOpenTransport?.(selectedReport.currentTransportId)}
                        disabled={!selectedReport.currentTransportId}
                        className="rounded-2xl border border-white/20 bg-white/10 px-3 py-2 text-sm font-semibold text-white transition hover:bg-white/18 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Editar ficha
                      </button>
                      <button
                        type="button"
                        onClick={() => onOpenTransport?.(selectedReport.currentTransportId)}
                        disabled={!selectedReport.currentTransportId}
                        className="rounded-2xl border border-cyan-200/30 bg-cyan-300/12 px-3 py-2 text-sm font-semibold text-cyan-50 transition hover:bg-cyan-300/22 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Cambiar foto
                      </button>
                      <button
                        type="button"
                        onClick={() => onOpenRouteSheet?.(selectedReport.currentTransportId)}
                        disabled={!selectedReport.currentTransportId}
                        className="rounded-2xl border border-white/20 bg-white/10 px-3 py-2 text-sm font-semibold text-white transition hover:bg-white/18 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Nueva ruta
                      </button>
                      <button
                        type="button"
                        onClick={() => onCopyAccess?.(selectedReport.accessPath)}
                        disabled={!selectedReport.accessPath}
                        className="rounded-2xl border border-white/20 bg-white/10 px-3 py-2 text-sm font-semibold text-white transition hover:bg-white/18 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Copiar acceso
                      </button>
                    </div>
                  </div>

                  <div id="drivers-board-kpis" className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                    {selectedReport.kpis.map((item, index) => {
                      const isRoutesCard = index === 0;
                      const activeRoutesCard = isRoutesCard && showRouteHistory;
                      const Tag = isRoutesCard ? 'button' : 'div';
                      return (
                        <Tag
                          key={item.label}
                          type={isRoutesCard ? 'button' : undefined}
                          onClick={isRoutesCard ? () => setShowRouteHistory((current) => !current) : undefined}
                          className={`rounded-[22px] border px-4 py-3 text-left shadow-[0_12px_28px_rgba(15,23,42,0.05)] ${
                            activeRoutesCard
                              ? 'border-sky-300 bg-sky-50'
                              : 'border-slate-200 bg-slate-50/90'
                          } ${isRoutesCard ? 'transition hover:border-sky-300 hover:bg-sky-50' : ''}`}
                        >
                          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{item.label}</p>
                          <p className="mt-2 text-2xl font-black tracking-[-0.04em] text-slate-900">{item.value}</p>
                          <p className="mt-1 text-xs text-slate-500">
                            {isRoutesCard ? 'Tocá para ver el listado filtrado por chofer.' : item.helper}
                          </p>
                        </Tag>
                      );
                    })}
                  </div>

                  {showRouteHistory ? (
                    <div id="drivers-board-route-history" className="rounded-[24px] border border-cyan-100 bg-[linear-gradient(180deg,#f8fafc_0%,#ecfeff_100%)] p-4 shadow-[0_12px_26px_rgba(8,145,178,0.08)]">
                      <div className="relative overflow-hidden flex items-center justify-between gap-3 rounded-[20px] border border-cyan-100 bg-[linear-gradient(135deg,#ffffff_0%,#ecfeff_100%)] px-4 py-3 shadow-[0_10px_24px_rgba(8,145,178,0.08)]">
                        <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(120deg,rgba(34,211,238,0.06)_0%,transparent_40%,rgba(255,255,255,0.40)_100%)]" />
                        <span className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 animate-[moduleShine_10.8s_ease-in-out_infinite]" style={{ background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.65) 45%, transparent 100%)", transform: "skewX(-18deg)" }} />
                        <div className="relative z-10">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Recorridos del chofer</p>
                          <h4 className="mt-1 text-base font-black tracking-[-0.03em] text-slate-900">Historial filtrado por chofer</h4>
                        </div>
                        <span className="relative z-10 rounded-full border border-cyan-100 bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-cyan-700">
                          {selectedReport.routeHistory.length} visibles
                        </span>
                      </div>
                      <div className="mt-3 space-y-2">
                        {selectedReport.routeHistory.length ? (
                          selectedReport.routeHistory.map((route) => (
                            <div key={route.id} className="rounded-[22px] border border-slate-200 bg-white px-3 py-3 shadow-[0_10px_22px_rgba(15,23,42,0.05)]">
                              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                <div className="flex min-w-0 gap-3">
                                  <div className="flex w-[92px] shrink-0 flex-col items-center justify-center rounded-[18px] border border-sky-100 bg-[linear-gradient(180deg,#f0f9ff_0%,#e0f2fe_100%)] px-2 py-2 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.82)]">
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-sky-500">Fecha</p>
                                    <p className="mt-1 text-[15px] font-black leading-tight tracking-[-0.03em] text-slate-900">{route.dateLabel}</p>
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <p className="truncate text-[14px] font-black tracking-[-0.03em] text-slate-900">{route.name}</p>
                                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">
                                        {route.statusLabel}
                                      </span>
                                    </div>
                                    <p className="mt-1 text-[12px] text-slate-500">{route.transportLabel}</p>
                                    <div className="mt-2 flex flex-wrap gap-1.5 text-[10px] text-slate-500">
                                      <span className="rounded-full bg-slate-50 px-2.5 py-1 font-semibold text-slate-700">{route.kmLabel}</span>
                                      <span className="rounded-full bg-slate-50 px-2.5 py-1 font-semibold text-slate-700">{route.destinationsLabel}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex shrink-0 justify-end lg:pt-1">
                                  <button
                                    type="button"
                                    onClick={() => onOpenRouteMonitor?.(route.id, route.transportId)}
                                    disabled={!route.transportId}
                                    className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.12em] text-sky-700 transition hover:border-sky-300 hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-45"
                                  >
                                    Ir a monitor de ese día
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="rounded-[22px] border border-dashed border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
                            Este chofer todavía no tiene recorridos históricos para mostrar.
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}

                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr),320px]">
                    <div className="rounded-[24px] border border-slate-200 bg-slate-50/80 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Informe del chofer</p>
                          <h4 className="mt-1 text-lg font-black tracking-[-0.03em] text-slate-900">Lectura operativa y económica</h4>
                        </div>
                        <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                          Alta {selectedReport.firstSeenLabel}
                        </span>
                      </div>

                      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="rounded-[22px] border border-cyan-100 bg-[linear-gradient(180deg,#ffffff_0%,#f0fdfa_100%)] px-4 py-3 shadow-[0_10px_24px_rgba(8,145,178,0.06)]">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Tipos de transporte usados</p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {selectedReport.transportTypeLabels.length ? (
                              selectedReport.transportTypeLabels.map((label) => (
                                <span key={label} className="inline-flex items-center rounded-full bg-slate-900 px-3 py-1 text-[11px] font-bold text-white">
                                  {label}
                                </span>
                              ))
                            ) : (
                              <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-500">
                                Sin registros todavía
                              </span>
                            )}
                          </div>
                          <p className="mt-3 text-xs text-slate-500">
                            {selectedReport.transportTypeCount} {selectedReport.transportTypeCount === 1 ? 'tipo de unidad utilizado' : 'tipos de unidad utilizados'}
                          </p>
                        </div>

                        <div className="rounded-[22px] border border-cyan-100 bg-[linear-gradient(180deg,#ffffff_0%,#f0fdfa_100%)] px-4 py-3 shadow-[0_10px_24px_rgba(8,145,178,0.06)]">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Impactos registrados</p>
                          <div className="mt-3 space-y-2">
                            <div className="flex items-center justify-between rounded-2xl border border-red-100 bg-red-50 px-3 py-2">
                              <div>
                                <p className="text-sm font-bold text-red-700">Multas</p>
                                <p className="text-xs text-red-500">{selectedReport.multasCount} registros</p>
                              </div>
                              <span className="text-sm font-black text-red-700">${formatCurrency(selectedReport.multasAmount)}</span>
                            </div>
                            <div className="flex items-center justify-between rounded-2xl border border-fuchsia-100 bg-fuchsia-50 px-3 py-2">
                              <div>
                                <p className="text-sm font-bold text-fuchsia-700">Incidentes</p>
                                <p className="text-xs text-fuchsia-500">{selectedReport.incidentesCount} registros</p>
                              </div>
                              <span className="text-sm font-black text-fuchsia-700">${formatCurrency(selectedReport.incidentesAmount)}</span>
                            </div>
                          </div>
                        </div>

                        <div className="rounded-[22px] border border-cyan-100 bg-[linear-gradient(180deg,#ffffff_0%,#f0fdfa_100%)] px-4 py-3 shadow-[0_10px_24px_rgba(8,145,178,0.06)] md:col-span-2">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Liquidación mensual</p>
                              <p className="mt-1 text-sm text-slate-500">Lectura rápida de lo que viene cobrando por período.</p>
                            </div>
                            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-700">
                              Acumulado ${formatCurrency(selectedReport.totalEarnings)}
                            </span>
                          </div>
                          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2">
                              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-600">Acumulado total</p>
                              <p className="mt-1 text-base font-black tracking-[-0.03em] text-emerald-900">${formatCurrency(selectedReport.totalEarnings)}</p>
                            </div>
                            {selectedReport.monthlyEarnings.length ? (
                              selectedReport.monthlyEarnings.map((entry) => (
                                <div key={entry.label} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
                                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{entry.label}</p>
                                  <p className="mt-1 text-base font-black tracking-[-0.03em] text-slate-900">${formatCurrency(entry.amount)}</p>
                                </div>
                              ))
                            ) : (
                              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-500">
                                Todavía no hay liquidación acumulada para mostrar en esta ficha.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div id="drivers-board-recent-history" className="rounded-[24px] border border-sky-100 bg-[linear-gradient(180deg,#ffffff_0%,#f0f9ff_100%)] p-3.5 shadow-[0_12px_28px_rgba(14,165,233,0.08)]">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Historial del chofer</p>
                          <h4 className="mt-1 text-base font-black tracking-[-0.03em] text-slate-900">Historia reciente</h4>
                        </div>
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                          {selectedReport.routeHistory.length} registros
                        </span>
                      </div>
                      <div className="mt-3 space-y-2">
                        {selectedReport.recentRoutes.length ? (
                          <>
                            {selectedReport.recentRoutes.map((route) => (
                              <div key={route.id} className="rounded-[20px] border border-slate-200 bg-slate-50 px-3 py-3 shadow-[0_10px_20px_rgba(15,23,42,0.05)]">
                                <div className="grid grid-cols-1 gap-3 md:grid-cols-[88px,minmax(0,1fr)] md:items-center">
                                  <div className="flex h-full min-h-[72px] shrink-0 flex-col items-center justify-center rounded-[16px] border border-sky-100 bg-white px-2 py-2 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.82)]">
                                    <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-sky-500">Fecha</p>
                                    <p className="mt-1 text-[15px] font-black leading-tight tracking-[-0.03em] text-slate-900">{route.dateLabel}</p>
                                  </div>
                                  <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <p className="truncate text-[13px] font-black tracking-[-0.03em] text-slate-900">{route.name}</p>
                                      <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">
                                        {route.statusLabel}
                                      </span>
                                    </div>
                                    <p className="mt-1 text-[11px] text-slate-500">{route.transportLabel}</p>
                                    <div className="mt-2 flex flex-wrap gap-1.5 text-[10px] text-slate-500">
                                      <span className="rounded-full bg-white px-2 py-0.5 font-semibold text-slate-700">
                                        {route.kmLabel}
                                      </span>
                                      <span className="rounded-full bg-white px-2 py-0.5 font-semibold text-slate-700">
                                        {route.destinationsLabel}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {selectedReport.routeHistory.length > selectedReport.recentRoutes.length ? (
                              <button
                                type="button"
                                onClick={() => setShowRouteHistory(true)}
                                className="inline-flex w-full items-center justify-center rounded-[18px] border border-slate-200 bg-white px-3 py-2 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                              >
                                Ver todas las historias
                              </button>
                            ) : null}
                          </>
                        ) : (
                          <div className="rounded-[22px] border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                            Este chofer todavía no tiene recorridos históricos para mostrar.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-[28px] border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center text-slate-500">
                  Seleccioná un chofer para abrir su lectura operativa.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

        {tutorialRun ? (
          <div
            ref={tutorialShortcutPanelRef}
            className="fixed right-4 top-24 z-[1401] transition-all duration-300"
          >
            <button
              type="button"
              onClick={() => setTutorialShortcutOpen((current) => !current)}
              className="mb-2 inline-flex items-center gap-2 rounded-2xl border border-cyan-300/30 bg-slate-950/86 px-4 py-2 text-xs font-black uppercase tracking-[0.14em] text-cyan-100 shadow-[0_18px_40px_rgba(2,6,23,0.42)] backdrop-blur-xl"
            >
              <Icon path="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" className="h-4 w-4" />
              {tutorialShortcutOpen ? 'Ocultar pasos' : 'Ver pasos'}
            </button>
            <div className={`overflow-hidden rounded-2xl border border-cyan-300/20 bg-slate-950/88 shadow-[0_18px_40px_rgba(2,6,23,0.42)] backdrop-blur-xl transition-all duration-300 ${tutorialShortcutOpen ? 'max-h-[70vh] w-[288px] opacity-100' : 'max-h-0 w-[288px] opacity-0'}`}>
              <div className="border-b border-white/10 px-4 py-3">
                <p className="text-[10px] font-black uppercase tracking-[0.18em] text-cyan-200">Tutorial de choferes</p>
                <p className="mt-1 text-sm font-semibold text-white">Recorrido guiado de la base operativa</p>
              </div>
              <div className="max-h-[calc(70vh-72px)] overflow-y-auto px-2 py-2">
                {tutorialSteps.map((step, index) => (
                  <button
                    key={step.target}
                    type="button"
                    onClick={() => jumpToTutorialStep(index)}
                    className={`w-full rounded-2xl px-3 py-3 text-left transition ${index === tutorialStepIndex ? 'bg-cyan-400/12' : 'hover:bg-white/5'}`}
                  >
                    <span className={`block text-[10px] font-bold uppercase tracking-[0.16em] ${index === tutorialStepIndex ? 'text-cyan-200' : 'text-slate-500'}`}>Paso {index + 1}</span>
                    <span className={`mt-1 block text-sm font-semibold ${index === tutorialStepIndex ? 'text-white' : 'text-slate-200'}`}>{step.title}</span>
                    <span className="mt-1 block text-xs leading-5 text-slate-400">{step.content}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : null}

      </section>
    </>
  );
};


const createDefaultLiquidationFormState = () => ({
  mode: 'inherit',
  customSettings: {
    pricePerKm: '',
    baseRouteFee: '',
    minimumRouteFee: ''
  }
});

const buildLiquidationFormState = (transport) => {
  const base = createDefaultLiquidationFormState();
  const mode = transport?.liquidation?.mode === 'custom' ? 'custom' : 'inherit';
  const customSettings = transport?.liquidation?.customSettings || {};

  return {
    mode,
    customSettings: {
      ...base.customSettings,
      pricePerKm:
        customSettings.pricePerKm === undefined || customSettings.pricePerKm === null
          ? ''
          : customSettings.pricePerKm,
      baseRouteFee:
        customSettings.baseRouteFee === undefined || customSettings.baseRouteFee === null
          ? ''
          : customSettings.baseRouteFee,
      minimumRouteFee:
        customSettings.minimumRouteFee === undefined || customSettings.minimumRouteFee === null
          ? ''
          : customSettings.minimumRouteFee
    }
  };
};

const normalizeLiquidationPayload = (liquidation = {}) => {
  const mode = liquidation?.mode === 'custom' ? 'custom' : 'inherit';

  if (mode !== 'custom') {
    return {
      mode: 'inherit',
      customSettings: null
    };
  }

  const custom = liquidation?.customSettings || {};

  return {
    mode: 'custom',
    customSettings: {
      costModel: 'perKm',
      pricePerKm: Number(custom.pricePerKm || 0),
      baseRouteFee: Number(custom.baseRouteFee || 0),
      minimumRouteFee: Number(custom.minimumRouteFee || 0),
      costScales: []
    }
  };
};

const getTypeLiquidationConfig = (companyLiquidationSettings, transportType) => {
  if (!transportType) return null;
  return companyLiquidationSettings?.transports?.[transportType] || null;
};

const getTransportPricingInfo = (transport, companyLiquidationSettings) => {
  const customMode = transport?.liquidation?.mode === 'custom';
  const customSettings = transport?.liquidation?.customSettings || {};

  if (customMode) {
    return {
      badge: 'Tarifa propia',
      detail: `$ ${formatCurrency(customSettings.pricePerKm || 0)} / km`,
      subdetail:
        Number(customSettings.baseRouteFee || 0) > 0 || Number(customSettings.minimumRouteFee || 0) > 0
          ? `Base $ ${formatCurrency(customSettings.baseRouteFee || 0)} · Mín. $ ${formatCurrency(customSettings.minimumRouteFee || 0)}`
          : 'Liquidación personalizada por unidad'
    };
  }

  const typeConfig = getTypeLiquidationConfig(companyLiquidationSettings, transport?.transportType);

  if (typeConfig) {
    return {
      badge: 'Heredada',
      detail:
        typeConfig.costModel === 'byScale'
          ? `Usa escala de ${transport?.transportType || 'tipo'}`
          : `$ ${formatCurrency(typeConfig.pricePerKm || 0)} / km`,
      subdetail: `Toma la configuración del tipo: ${transport?.transportType || 'sin tipo'}`
    };
  }

  return {
    badge: 'Sin config',
    detail: 'Usa fallback global',
    subdetail: 'Todavía no hay tarifa definida para este tipo'
  };
};

const normalizeCompanyId = (companyId = DEFAULT_COMPANY_ID) => {
  const value = String(companyId || DEFAULT_COMPANY_ID).trim();
  return value || DEFAULT_COMPANY_ID;
};

const hexToRgb = (hex = '') => {
  const normalized = String(hex || '').replace('#', '').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return null;
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16)
  };
};

const getColorDistance = (first, second) => {
  const a = hexToRgb(first);
  const b = hexToRgb(second);
  if (!a || !b) return Number.MAX_SAFE_INTEGER;
  return Math.sqrt(
    ((a.r - b.r) ** 2) +
    ((a.g - b.g) ** 2) +
    ((a.b - b.b) ** 2)
  );
};

const DEMO_TRANSPORT = {
  id: '__demo-unit__',
  name: 'Unidad demo',
  driverName: 'Chofer ejemplo',
  driverLastName: 'Demo',
  transportType: 'camioneta',
  phone: '11 5555-0101',
  slug: 'demo-unidad',
  patent: 'AE 000 AA',
  transportModel: 'Sprinter 311 CDI',
  vehicleLabel: 'Sprinter 311 CDI',
  isActive: true,
  companyName: 'Demo',
  identityColor: '#0ea5e9',
  identitySoftColor: '#e0f2fe'
};

const getDriverDisplayName = (transport = {}) => {
  return buildDriverDisplayName(transport, transport.name || 'Sin chofer');
};

const shouldUseLegacyTransportFallback = (companyId) => {
  return normalizeCompanyId(companyId) === DEFAULT_COMPANY_ID;
};

const buildTransportIdentityKeys = (transport = {}) => {
  const keys = new Set();

  if (transport.id) keys.add(`id:${transport.id}`);
  if (transport.legacySourceId) keys.add(`legacy:${transport.legacySourceId}`);
  if (transport.slug) keys.add(`slug:${slugify(transport.slug)}`);

  const composite = [transport.name, transport.driverName, transport.transportType]
    .filter(Boolean)
    .map((value) => slugify(value))
    .join(':');

  if (composite) keys.add(`composite:${composite}`);

  return Array.from(keys);
};

const mergeTransportSources = (companyDocs = [], legacyDocs = []) => {
  if (!legacyDocs.length) return companyDocs;
  if (!companyDocs.length) return legacyDocs;

  const companyKeys = new Set();
  companyDocs.forEach((transport) => {
    buildTransportIdentityKeys(transport).forEach((key) => companyKeys.add(key));
  });

  const merged = [...companyDocs];

  legacyDocs.forEach((legacyTransport) => {
    const alreadyMigrated = buildTransportIdentityKeys(legacyTransport).some((key) => companyKeys.has(key));
    if (!alreadyMigrated) {
      merged.push(legacyTransport);
    }
  });

  return merged;
};

const sortTransports = (transports = []) => {
  return [...transports].sort((a, b) => {
    const nameA = String(a?.name || '').toLowerCase();
    const nameB = String(b?.name || '').toLowerCase();
    return nameA.localeCompare(nameB, 'es');
  });
};

// Helpers visuales para el tablero de transportes
const getTransportIconPath = (transportType = 'camioneta') => {
  const normalized = String(transportType || 'camioneta').trim().toLowerCase();
  const catalog = {
    camioneta: 'M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25',
    moto: 'M15.75 10.5l4.724-4.724A3.375 3.375 0 0015.75 3h-2.25a3.375 3.375 0 00-3.375 3.375v2.25M9.375 10.5H5.25A2.25 2.25 0 003 12.75v4.248M9.375 10.5h-.75V8.25m-.75 0a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm9 0a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM7.5 15.75m-3 0a3 3 0 116 0 3 3 0 01-6 0zm-3 0h7.5',
    auto: 'M3.75 16.5v-1.125c0-.621.504-1.125 1.125-1.125h14.25c.621 0 1.125.504 1.125 1.125V16.5M4.5 12l1.32-3.301A2.25 2.25 0 017.909 7.5h8.182a2.25 2.25 0 012.089 1.199L19.5 12M6.75 16.5h.008v.008H6.75V16.5zm10.5 0h.008v.008h-.008V16.5z',
    camion: 'M1.5 13.5h12m0 0V6.75A2.25 2.25 0 0011.25 4.5h-6A2.25 2.25 0 003 6.75v6.75m10.5 0h3.879a1.5 1.5 0 001.06-.44l1.621-1.62a1.5 1.5 0 00.44-1.061V9.75a1.5 1.5 0 00-.44-1.06l-1.62-1.621a1.5 1.5 0 00-1.061-.44H13.5m-9 10.5a1.5 1.5 0 103 0 1.5 1.5 0 00-3 0zm12 0a1.5 1.5 0 103 0 1.5 1.5 0 00-3 0z'
  };

  return catalog[normalized] || 'M12 4.5v15m7.5-7.5h-15';
};

const getTransportTypeLabel = (transportType = '') => {
  const normalized = String(transportType || '').trim().toLowerCase();
  const labels = {
    moto: 'Moto',
    camioneta: 'Camioneta',
    auto: 'Auto',
    camion: 'Camión'
  };
  return labels[normalized] || 'Unidad';
};

const buildTransportTone = (transport = {}) => {
  const identity = resolveTransportIdentity(transport);
  const accentColor = identity.identityColor;
  const softColor = identity.identitySoftColor || mixHexColor(accentColor, '#ffffff', 0.84);
  const steelTop = mixHexColor('#f8fafc', accentColor, 0.06);
  const steelMid = mixHexColor('#d8e0e7', accentColor, 0.05);
  const steelBottom = mixHexColor('#edf2f7', accentColor, 0.04);
  const steelHighlight = mixHexColor('#ffffff', accentColor, 0.05);

  return {
    accentColor,
    softColor,
    iconPath: getTransportIconPath(transport.transportType),
    iconWrapStyle: {
      background: `linear-gradient(180deg, ${mixHexColor('#ffffff', accentColor, 0.08)} 0%, ${mixHexColor('#dce4ec', accentColor, 0.18)} 100%)`,
      borderColor: withAlpha(accentColor, 0.2),
      color: accentColor,
      boxShadow: `0 10px 22px ${withAlpha(accentColor, 0.16)}`
    },
    typeChipStyle: {
      backgroundColor: withAlpha(accentColor, 0.12),
      borderColor: withAlpha(accentColor, 0.2),
      color: accentColor
    },
    detailButtonStyle: {
      background: `linear-gradient(180deg, ${withAlpha(accentColor, 0.96)} 0%, ${mixHexColor(accentColor, '#0f172a', 0.18)} 100%)`,
      color: '#ffffff',
      boxShadow: `0 12px 24px ${withAlpha(accentColor, 0.18)}`
    },
    routeButtonStyle: {
      background: `
        linear-gradient(180deg, ${mixHexColor('#f8fafc', accentColor, 0.18)} 0%, ${mixHexColor('#cbd5e1', accentColor, 0.12)} 10%, ${mixHexColor(accentColor, '#0f172a', 0.08)} 18%, ${mixHexColor(accentColor, '#0f172a', 0.12)} 42%, ${mixHexColor(accentColor, '#0f172a', 0.22)} 72%, ${mixHexColor(accentColor, '#020617', 0.34)} 100%),
        repeating-linear-gradient(108deg, rgba(255,255,255,0.12) 0px, rgba(255,255,255,0.05) 2px, rgba(15,23,42,0.06) 5px, rgba(255,255,255,0.02) 8px)
      `,
      color: '#ffffff',
      borderColor: withAlpha(mixHexColor(accentColor, '#020617', 0.24), 0.72),
      boxShadow: `inset 0 1px 0 rgba(255,255,255,0.24), inset 0 -1px 0 rgba(2,6,23,0.28), 0 24px 40px ${withAlpha(mixHexColor(accentColor, '#020617', 0.18), 0.3)}, 0 12px 18px rgba(15,23,42,0.22)`
    },
    fieldStyle: {
      background: `linear-gradient(180deg, ${mixHexColor('#ffffff', accentColor, 0.03)} 0%, ${mixHexColor('#e8eef4', accentColor, 0.08)} 100%)`,
      borderColor: withAlpha(accentColor, 0.12)
    },
    surfaceStyle: {
      backgroundImage: `
        linear-gradient(180deg, ${mixHexColor('#ffffff', accentColor, 0.03)} 0%, ${steelHighlight} 8%, ${steelTop} 20%, ${steelMid} 56%, ${steelBottom} 100%),
        radial-gradient(circle at 12% 14%, ${withAlpha(accentColor, 0.18)} 0%, ${withAlpha(accentColor, 0.08)} 18%, transparent 54%),
        linear-gradient(120deg, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0.06) 18%, transparent 38%),
        repeating-linear-gradient(102deg, rgba(255,255,255,0.20) 0px, rgba(255,255,255,0.10) 2px, rgba(148,163,184,0.08) 4px, rgba(255,255,255,0.03) 7px, rgba(148,163,184,0.05) 10px)
      `,
      backgroundBlendMode: 'normal, overlay, screen, soft-light',
      boxShadow: `inset 0 1px 0 rgba(255,255,255,0.82), inset 0 -1px 0 rgba(148,163,184,0.22), inset 0 18px 24px rgba(255,255,255,0.10)`
    }
  };
};

const getTransportAvatarLabel = (transport = {}) => {
  const base = String(getDriverDisplayName(transport) || transport.name || transport.transportModel || transport.vehicleLabel || 'TR')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');

  return base || 'TR';
};

const TransportAvatar = ({ transport, size = 'md' }) => {
  const tone = buildTransportTone(transport);
  const label = getTransportAvatarLabel(transport);
  const photoURL = typeof transport?.currentDriverPhotoURL === 'string' && transport.currentDriverPhotoURL.trim()
    ? transport.currentDriverPhotoURL.trim()
    : typeof transport?.photoURL === 'string'
      ? transport.photoURL.trim()
      : '';

  const sizeClasses = {
    sm: 'h-14 w-14 rounded-[18px] text-sm',
    md: 'h-[4.25rem] w-[4.25rem] rounded-[22px] text-base',
    lg: 'h-24 w-24 rounded-[28px] text-xl'
  };

  const frameClass = sizeClasses[size] || sizeClasses.md;

  return (
    <div
      className={`relative shrink-0 overflow-hidden border ${frameClass}`}
      style={{
        borderColor: withAlpha(tone.accentColor, 0.22),
        background: `
          linear-gradient(180deg, rgba(255,255,255,0.96) 0%, ${mixHexColor('#d7dee7', tone.accentColor, 0.14)} 100%),
          repeating-linear-gradient(110deg, rgba(255,255,255,0.10) 0px, rgba(255,255,255,0.04) 2px, rgba(148,163,184,0.06) 5px, rgba(255,255,255,0.02) 9px)
        `,
        boxShadow: `0 12px 24px ${withAlpha(tone.accentColor, 0.16)}, inset 0 1px 0 rgba(255,255,255,0.82), inset 0 -1px 0 rgba(148,163,184,0.22), inset 0 10px 18px rgba(255,255,255,0.10)`
      }}
    >
      <div className="pointer-events-none absolute inset-[3px] rounded-[inherit] border border-white/60" />
      {photoURL ? (
        <>
          <img
            src={photoURL}
            alt={getDriverDisplayName(transport) || transport?.name || 'Transportista'}
            className="driver-photo-img relative z-10 h-full w-full object-cover"
          />
          <span className="driver-photo-glow" />
        </>
      ) : (
        <div
          className="relative z-10 flex h-full w-full items-center justify-center font-black uppercase tracking-[0.08em]"
          style={{
            color: mixHexColor(tone.accentColor, '#0f172a', 0.24),
            textShadow: '0 1px 0 rgba(255,255,255,0.45)'
          }}
        >
          {label}
        </div>
      )}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: 'linear-gradient(145deg, rgba(255,255,255,0.28) 0%, transparent 38%, rgba(15,23,42,0.10) 100%)'
        }}
      />
      <div
        className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 animate-[moduleShine_2.2s_ease-in-out_infinite]"
        style={{
          background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.42) 45%, transparent 100%)',
          transform: 'skewX(-18deg)'
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background: `radial-gradient(circle at 24% 18%, ${withAlpha(tone.accentColor, 0.16)} 0%, transparent 28%)`
        }}
      />
    </div>
  );
};

const TransportDetailSurface = ({
  transport,
  pricingInfo,
  routeCount,
  onEdit,
  onOpenRouteSheet,
  moduleActions = [],
  recentActions = [],
  compact = false
}) => {
  const driverLabel = getDriverDisplayName(transport);
  const typeLabel = getTransportTypeLabel(transport.transportType);
  const tone = buildTransportTone(transport);
  const monitorAction = moduleActions.find((action) => action.id === 'rutaActual');
  const newRouteAction = moduleActions.find((action) => action.id === 'rutaNueva');
  const historyAction = moduleActions.find((action) => action.id === 'historial');
  const driverAccessUrl = buildOperationalAccessUrl({ driverAccessSlug: transport.driverAccessSlug || transport.slug });
  const detailFields = [
    { label: 'Acceso del chofer', value: driverAccessUrl || 'sin acceso', mono: true },
    (transport.driverPassword || transport.password) ? { label: 'Clave del chofer', value: transport.driverPassword || transport.password, mono: true } : null,
    { label: 'Alias interno', value: transport.name || 'Sin alias' },
    { label: 'Chofer', value: driverLabel },
    { label: 'Móvil', value: typeLabel },
    (transport.transportModel || transport.vehicleLabel) ? { label: 'Modelo', value: transport.transportModel || transport.vehicleLabel } : null,
    transport.patent ? { label: 'Patente', value: String(transport.patent).toUpperCase() } : null,
    (transport.currentDriverPhone || transport.phone) ? { label: 'Teléfono', value: transport.currentDriverPhone || transport.phone } : null,
    { label: 'Estado', value: transport.transportStatus === 'assigned' ? 'Asignada' : transport.transportStatus === 'parking' ? 'En depósito' : transport.transportStatus === 'in_route' ? 'En ruta' : transport.transportStatus === 'service' ? 'Fuera de servicio' : transport.isActive === false || transport.transportStatus === 'inactive' ? 'Inactiva' : 'Disponible' },
    { label: 'Rutas activas', value: String(routeCount || 0) },
    pricingInfo?.badge ? { label: 'Liquidación', value: pricingInfo.badge } : null
  ].filter(Boolean);
  const normalizedRecentActions = Array.isArray(recentActions) ? recentActions.slice(0, 4) : [];
  const recentActionsPanel = normalizedRecentActions.length ? (
    <div className={`rounded-[22px] border ${compact ? 'border-slate-300/85 bg-white p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.74)]' : 'border-white/10 bg-white/[0.045] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-sm'}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className={`text-[9px] font-semibold uppercase tracking-[0.18em] ${compact ? 'text-slate-500' : 'text-slate-400'}`}>Últimas acciones</p>
          <p className={`mt-0.5 text-[11px] font-medium ${compact ? 'text-slate-600' : 'text-slate-300/90'}`}>
            Foto, QR y código de barras quedan trazados en la ficha operativa de esta unidad.
          </p>
        </div>
        <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${compact ? 'border-slate-200 bg-slate-50 text-slate-600' : 'border-white/10 bg-white/10 text-slate-200'}`}>
          {normalizedRecentActions.length} evento{normalizedRecentActions.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className="mt-3 space-y-2.5">
        {normalizedRecentActions.map((event) => {
          const actionMeta = buildTraceActionMeta(event);
          const routeLabel = buildTransportDisplayName(event?._route || {});
          const itemToneClasses =
            actionMeta.tone === 'cyan'
              ? compact ? 'border-cyan-200 bg-cyan-50 text-cyan-700' : 'border-cyan-400/30 bg-cyan-400/10 text-cyan-100'
              : actionMeta.tone === 'emerald'
                ? compact ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-emerald-400/30 bg-emerald-400/10 text-emerald-100'
                : actionMeta.tone === 'amber'
                  ? compact ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-amber-300/30 bg-amber-300/10 text-amber-100'
                  : actionMeta.tone === 'rose'
                    ? compact ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-rose-300/30 bg-rose-300/10 text-rose-100'
                    : compact ? 'border-slate-200 bg-slate-50 text-slate-700' : 'border-white/10 bg-white/10 text-slate-100';

          return (
            <div
              key={event.id}
              className={`flex items-start gap-3 rounded-[18px] border px-3 py-2.5 ${compact ? 'shadow-sm' : 'shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]'} ${compact ? 'border-slate-200 bg-slate-50/70' : 'border-white/10 bg-white/[0.05]'}`}
            >
              {event?.evidenceUrl ? (
                <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/80 shadow-sm">
                  <img
                    src={String(event.evidenceUrl)}
                    alt="Evidencia operativa"
                    className="h-14 w-16 object-cover"
                  />
                </div>
              ) : (
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border ${itemToneClasses}`}>
                  <Icon path={actionMeta.iconPath} className="h-4 w-4" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.16em] ${itemToneClasses}`}>
                    {actionMeta.badge}
                  </span>
                  <span className={`text-[10px] font-semibold uppercase tracking-[0.14em] ${compact ? 'text-slate-500' : 'text-slate-300/80'}`}>
                    {formatActionDateTime(event.createdAt)}
                  </span>
                </div>
                <p className={`mt-1 text-[13px] font-semibold ${compact ? 'text-slate-900' : 'text-white'}`}>
                  {actionMeta.title}
                </p>
                <p className={`mt-0.5 text-xs leading-relaxed ${compact ? 'text-slate-600' : 'text-slate-300/90'}`}>
                  {actionMeta.detail}
                </p>
                <div className={`mt-1.5 flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.14em] ${compact ? 'text-slate-500' : 'text-slate-300/80'}`}>
                  {event?.destinationName ? (
                    <span className={`rounded-full border px-2 py-0.5 ${compact ? 'border-slate-200 bg-white text-slate-600' : 'border-white/10 bg-white/10 text-slate-100'}`}>
                      {event.destinationName}
                    </span>
                  ) : null}
                  {routeLabel ? (
                    <span className={`rounded-full border px-2 py-0.5 ${compact ? 'border-slate-200 bg-white text-slate-600' : 'border-white/10 bg-white/10 text-slate-100'}`}>
                      {routeLabel}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  ) : null;

  if (compact) {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {detailFields.map((field) => (
            <div
              key={field.label}
              className="rounded-[18px] border px-3.5 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.72)]"
              style={{ ...tone.fieldStyle, backgroundColor: '#ffffff' }}
            >
              <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-400">{field.label}</p>
              <div className="mt-1.5 flex items-start justify-between gap-2">
                <p className={`min-w-0 flex-1 text-[12px] font-semibold text-slate-800 ${field.mono ? 'break-all font-mono text-[11px]' : ''}`}>{field.value}</p>
                {field.label === 'Acceso del chofer' && (transport?.driverAccessSlug || transport?.slug) ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard?.writeText(driverAccessUrl);
                    }}
                    className="shrink-0 rounded-full border border-slate-200 bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-600 hover:bg-slate-50"
                  >
                    Copiar
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => onEdit?.(transport.id)}
            className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-[16px] border border-slate-200 bg-white px-4 text-[12px] font-semibold text-slate-700 shadow-sm transition-all duration-300 hover:-translate-y-0.5"
          >
            <Icon path="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" className="h-4 w-4" />
            <span>Editar ficha</span>
          </button>
          <button
            type="button"
            onClick={() => newRouteAction?.onClick?.()}
            disabled={newRouteAction?.disabled}
            className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-[16px] border px-4 text-[12px] font-semibold transition-all duration-300 hover:-translate-y-0.5"
            style={tone.routeButtonStyle}
          >
            <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
            <span>Crear ruta</span>
          </button>
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => monitorAction?.onClick?.()}
            disabled={monitorAction?.disabled}
            className="rounded-[18px] border px-3.5 py-3 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.72)] transition-all duration-300 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-45"
            style={{ ...tone.fieldStyle, backgroundColor: '#ffffff' }}
          >
            <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-400">Ver ruta activa</p>
            <p className="mt-1 text-[13px] font-bold text-slate-900">
              {monitorAction?.disabled ? 'Sin ruta activa' : 'Entrar al monitor'}
            </p>
          </button>
          <button
            type="button"
            onClick={() => historyAction?.onClick?.()}
            disabled={historyAction?.disabled}
            className="rounded-[18px] border px-3.5 py-3 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.72)] transition-all duration-300 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-45"
            style={{ ...tone.fieldStyle, backgroundColor: '#ffffff' }}
          >
            <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-400">Ver historial</p>
            <p className="mt-1 text-[13px] font-bold text-slate-900">Calendario y rutas</p>
          </button>
        </div>

        {recentActionsPanel}
      </div>
    );
  }

  return (
    <div className="relative h-full min-h-[560px] overflow-hidden px-6 py-6 text-slate-100 md:px-7 xl:px-8">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.08),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(59,130,246,0.10),transparent_30%)]" />
      <div className="pointer-events-none absolute -left-8 top-10 h-28 w-28 rounded-full blur-3xl" style={{ backgroundColor: withAlpha(tone.accentColor, 0.22) }} />
      <div className="pointer-events-none absolute bottom-10 right-0 text-[72px] font-black uppercase tracking-[0.32em] text-white/[0.035]">
        Detalle
      </div>

      <div className="relative z-10 flex h-full flex-col">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <TransportAvatar transport={transport} size="lg" />
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">Detalle operativo</p>
              <h3 className="mt-3 text-2xl font-bold tracking-[-0.03em] text-white">{driverLabel}</h3>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span
                  className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em]"
                  style={tone.typeChipStyle}
                >
                  <span className="inline-flex h-2 w-2 rounded-full" style={{ backgroundColor: tone.accentColor }} />
                  {typeLabel}
                </span>
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold text-slate-200">
                  {transport.isActive === false ? 'Inactivo' : 'Disponible'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[18px] border" style={tone.iconWrapStyle}>
            <Icon path={tone.iconPath} className="h-7 w-7" />
          </div>
        </div>

        <div className="mt-6 grid gap-3 text-slate-100 sm:grid-cols-2">
          {detailFields.map((field) => (
            <div
              key={field.label}
              className="rounded-[20px] border border-white/10 bg-white/[0.045] px-4 py-3.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-sm"
            >
              <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-400">{field.label}</p>
              <div className="mt-1.5 flex items-start justify-between gap-2">
                <p className={`min-w-0 flex-1 text-[13px] font-semibold text-white ${field.mono ? 'break-all font-mono text-[11px]' : ''}`}>{field.value}</p>
                {field.label === 'Acceso del chofer' && (transport?.driverAccessSlug || transport?.slug) ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard?.writeText(driverAccessUrl);
                    }}
                    className="shrink-0 rounded-full border border-white/12 bg-white/10 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-100 transition hover:bg-white/15"
                  >
                    Copiar
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 rounded-[24px] border border-white/10 bg-white/[0.045] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Liquidación</p>
              <p className="mt-1 text-lg font-semibold text-white">{pricingInfo?.detail || 'Sin datos de liquidación'}</p>
            </div>
            <span className="rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]" style={tone.typeChipStyle}>
              {pricingInfo?.badge || 'N/A'}
            </span>
          </div>
          {pricingInfo?.subdetail ? (
            <p className="mt-2 text-sm leading-relaxed text-slate-300">{pricingInfo.subdetail}</p>
          ) : null}
        </div>

        <div className="mt-5">
          <TransportModuleActions transport={transport} tone={tone} moduleActions={moduleActions} />
        </div>

        {recentActionsPanel ? (
          <div className="mt-5">
            {recentActionsPanel}
          </div>
        ) : null}

        <div className="mt-auto grid gap-3 pt-6 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => onEdit?.(transport.id)}
            className="inline-flex min-h-[50px] items-center justify-center gap-2 rounded-[18px] border border-white/10 bg-white/[0.06] px-4 text-[12px] font-semibold text-white shadow-[0_18px_35px_rgba(2,6,23,0.22)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/[0.09]"
          >
            <Icon path="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" className="h-4 w-4" />
            <span>Editar ficha</span>
          </button>
          <button
            type="button"
            onClick={() => newRouteAction?.onClick?.()}
            disabled={newRouteAction?.disabled}
            className="inline-flex min-h-[50px] items-center justify-center gap-2 rounded-[18px] border px-4 text-[12px] font-semibold transition-all duration-300 hover:-translate-y-0.5"
            style={tone.detailButtonStyle}
          >
            <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
            <span>Crear ruta</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const TransportCard = ({
  transport,
  contractClients = [],
  onToggleUnit,
  onOpenDetail,
  onOpenRouteSheet,
  routeCount,
  pricingInfo,
  recentActions = [],
  isUnitOpen,
  isDetailOpen,
  onEdit,
  moduleActions = [],
  tutorialIds = null
}) => {
  const driverLabel = getDriverDisplayName(transport);
  const typeLabel = getTransportTypeLabel(transport.transportType);
  const tone = buildTransportTone(transport);
  const accessLabel = buildOperationalAccessUrl({ driverAccessSlug: transport.driverAccessSlug || transport.slug }) || 'Sin acceso generado';
  const statusLabel = transport.transportStatus === 'assigned' ? 'Asignada' : transport.transportStatus === 'parking' ? 'En depósito' : transport.transportStatus === 'in_route' ? 'En ruta' : transport.transportStatus === 'service' ? 'Fuera de servicio' : transport.isActive === false || transport.transportStatus === 'inactive' ? 'Inactiva' : 'Operativa';
  const transportMeta = transport.transportModel || transport.vehicleLabel || transport.patent || transport.phone || accessLabel;
  const unitAlias = transport.name || 'Sin alias';
  const monitorAction = moduleActions.find((action) => action.id === 'rutaActual');
  const newRouteAction = moduleActions.find((action) => action.id === 'rutaNueva');
  const historyAction = moduleActions.find((action) => action.id === 'historial');
  const visibleRecentActions = Array.isArray(recentActions) ? recentActions.slice(0, 3) : [];
  const heroRecentAction = visibleRecentActions.length ? buildRecentActionPreview(visibleRecentActions[0]) : null;

  return (
    <article
      id={tutorialIds?.card}
      className="group relative overflow-hidden rounded-[30px] border border-slate-400/90 bg-transparent shadow-[0_28px_68px_rgba(15,23,42,0.2)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_40px_92px_rgba(15,23,42,0.24)] active:-translate-y-0.5 active:shadow-[0_40px_92px_rgba(15,23,42,0.24)]"
      style={{
        ...tone.surfaceStyle,
        backgroundImage: `
          linear-gradient(180deg, ${mixHexColor('#ffffff', tone.accentColor, 0.06)} 0%, ${mixHexColor('#eef3f8', tone.accentColor, 0.16)} 18%, ${mixHexColor('#d6dee8', tone.accentColor, 0.14)} 54%, ${mixHexColor('#cad5e2', tone.accentColor, 0.1)} 100%),
          radial-gradient(circle at 14% 12%, ${withAlpha(tone.accentColor, 0.2)} 0%, transparent 28%),
          linear-gradient(120deg, rgba(255,255,255,0.26) 0%, rgba(255,255,255,0.06) 18%, transparent 36%),
          repeating-linear-gradient(108deg, rgba(255,255,255,0.14) 0px, rgba(255,255,255,0.06) 2px, rgba(148,163,184,0.08) 5px, rgba(255,255,255,0.02) 8px)
        `
      }}
    >
      <div className="absolute inset-y-0 left-0 w-[4px]" style={{ background: `linear-gradient(180deg, ${tone.accentColor} 0%, ${mixHexColor(tone.accentColor, '#0f172a', 0.22)} 100%)` }} />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-white/75" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(140deg,rgba(255,255,255,0.30)_0%,rgba(255,255,255,0.10)_18%,transparent_36%),linear-gradient(180deg,rgba(255,255,255,0.12),transparent_32%)]" />
      <div className="pointer-events-none absolute -right-12 top-0 h-24 w-24 rounded-full blur-3xl" style={{ backgroundColor: withAlpha(tone.accentColor, 0.09) }} />
      <div
        className="pointer-events-none absolute inset-0 opacity-60 transition-opacity duration-300 group-hover:opacity-100 group-active:opacity-100"
        style={{
          background: `radial-gradient(circle at 12% 18%, ${withAlpha(tone.accentColor, 0.16)} 0%, transparent 28%), radial-gradient(circle at 88% 84%, ${withAlpha(tone.accentColor, 0.12)} 0%, transparent 26%)`
        }}
      />
      <div
        className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 opacity-70 transition-opacity duration-300 group-hover:opacity-100 group-active:opacity-100 animate-[moduleShine_6.2s_ease-in-out_infinite]"
        style={{
          background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.28) 52%, transparent 100%)',
          transform: 'skewX(-18deg)'
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-active:opacity-100"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.16) 0%, transparent 24%, transparent 76%, rgba(255,255,255,0.10) 100%)'
        }}
      />

      <div className="relative z-10 px-4 py-4 md:px-5 md:py-5">
        <div className="grid gap-2.5">
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(220px,240px)] lg:items-stretch">
            <button
              id={tutorialIds?.header}
              type="button"
              onClick={() => onToggleUnit?.(transport.id)}
              className={`grid h-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] rounded-[26px] border border-slate-500/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.78),rgba(219,228,237,0.36))] text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.88),inset_0_-1px_0_rgba(148,163,184,0.14),0_20px_38px_rgba(15,23,42,0.12)] transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:border-slate-600/80 ${isUnitOpen ? 'items-start gap-4 px-4 py-4' : 'items-center gap-3 px-3.5 py-3'}`}
              aria-expanded={isUnitOpen}
            >
              <TransportAvatar transport={transport} size={isUnitOpen ? 'lg' : 'md'} />

              <div className="min-w-0">
                <div className={`flex flex-wrap items-center gap-1.5 ${isUnitOpen ? '' : 'mb-1'}`}>
                  <span className="rounded-full border border-slate-300/90 bg-white/45 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-500">Ficha</span>
                  <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em]" style={tone.typeChipStyle}>
                    {typeLabel}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-slate-300/75 bg-white/34 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-600">
                    {statusLabel}
                  </span>
                </div>

                <h2 className={`min-w-0 truncate font-bold tracking-[-0.035em] text-slate-950 ${isUnitOpen ? 'mt-2 text-[18px]' : 'text-[16px]'}`}>{driverLabel}</h2>
                <p className={`truncate font-medium text-slate-600 ${isUnitOpen ? 'mt-1 text-[12px]' : 'text-[11px]'}`}>{transportMeta}</p>
                <div className={`mt-2 flex flex-wrap items-center gap-1.5 ${isUnitOpen ? 'text-[10px]' : 'text-[9px]'}`}>
                  <span className="inline-flex items-center rounded-full border border-white/80 bg-white/60 px-2.5 py-1 font-semibold tracking-[0.1em] text-slate-600 shadow-[inset_0_1px_0_rgba(255,255,255,0.84)]">
                    {unitAlias}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-slate-300/80 bg-white/40 px-2.5 py-1 font-semibold text-slate-600">
                    {routeCount} {routeCount === 1 ? 'ruta' : 'rutas'}
                  </span>
                  {visibleRecentActions.length ? (
                    <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50/90 px-2.5 py-1 font-semibold text-emerald-700">
                      {visibleRecentActions.length} acción{visibleRecentActions.length === 1 ? '' : 'es'} reciente{visibleRecentActions.length === 1 ? '' : 's'}
                    </span>
                  ) : null}
                  {contractClients.length ? (
                    <span className="inline-flex items-center rounded-full border border-cyan-200 bg-cyan-50/90 px-2.5 py-1 font-semibold text-cyan-700">
                      Fijo con {contractClients[0]}
                      {contractClients.length > 1 ? ` +${contractClients.length - 1}` : ''}
                    </span>
                  ) : null}
                </div>
                {contractClients.length ? (
                  <p className={`mt-2 line-clamp-2 text-cyan-700 ${isUnitOpen ? 'text-[11px]' : 'text-[10px]'}`}>
                    Servicio contratado para {contractClients.slice(0, 3).join(', ')}
                    {contractClients.length > 3 ? ` y ${contractClients.length - 3} más` : ''}.
                  </p>
                ) : null}
              </div>

              <div className={`flex items-center justify-center rounded-[18px] border border-white/80 bg-white/60 shadow-[inset_0_1px_0_rgba(255,255,255,0.82)] transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${isUnitOpen ? 'h-11 w-11 animate-[pulse_2.6s_ease-in-out_infinite]' : 'h-9 w-9 animate-pulse'}`}>
                <Icon
                  path={isUnitOpen ? 'M19.5 8.25l-7.5 7.5-7.5-7.5' : 'M8.25 4.5l7.5 7.5-7.5 7.5'}
                  className={`${isUnitOpen ? 'h-4 w-4' : 'h-3.5 w-3.5'} text-slate-700 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]`}
                />
              </div>
            </button>

            <div className="grid grid-cols-2 gap-2 lg:h-full lg:w-full lg:max-w-[240px] lg:grid-cols-1 lg:auto-rows-fr lg:justify-self-end">
              <button
                id={tutorialIds?.detail}
                type="button"
                onClick={() => onOpenDetail?.(transport.id)}
                className={`relative inline-flex min-h-[42px] items-center justify-center gap-2 overflow-hidden rounded-[15px] px-3 text-[10px] font-bold tracking-[-0.01em] transition-all duration-300 hover:-translate-y-0.5 lg:h-full ${isDetailOpen ? 'animate-[pulse_2.4s_ease-in-out_infinite]' : ''}`}
                style={tone.detailButtonStyle}
                aria-pressed={isDetailOpen}
              >
                <span className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 animate-[moduleShine_4.6s_ease-in-out_infinite]" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.24) 50%, transparent 100%)', transform: 'skewX(-18deg)' }} />
                <span>{isDetailOpen ? 'Cerrar' : 'Detalle'}</span>
              </button>
              <button
                id={tutorialIds?.create}
                type="button"
                onClick={() => newRouteAction?.onClick?.()}
                disabled={newRouteAction?.disabled}
                className="relative inline-flex min-h-[46px] items-center justify-center gap-2 overflow-hidden rounded-[16px] border px-3.5 text-[10px] font-black uppercase tracking-[0.08em] transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-45 lg:h-full"
                style={tone.routeButtonStyle}
              >
                <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.20)_0%,transparent_42%,rgba(15,23,42,0.08)_100%)]" />
                <span className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 animate-[moduleShine_4.8s_ease-in-out_infinite]" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.34) 50%, transparent 100%)', transform: 'skewX(-18deg)' }} />
                <span className="relative inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/25 bg-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]">
                  <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-3.5 w-3.5" />
                </span>
                  <span className="relative">Crear ruta</span>
              </button>

              {isUnitOpen && heroRecentAction ? (
                <div className="col-span-2 overflow-hidden rounded-[18px] border border-slate-300/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.82),rgba(241,245,249,0.58))] px-3.5 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.82),0_12px_28px_rgba(15,23,42,0.08)]">
                  <div className="flex items-start gap-3">
                    {visibleRecentActions[0]?.evidenceUrl ? (
                      <a
                        href={String(visibleRecentActions[0].evidenceUrl)}
                        target="_blank"
                        rel="noreferrer"
                        className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5"
                        title="Abrir evidencia operativa"
                      >
                        <img
                          src={String(visibleRecentActions[0].evidenceUrl)}
                          alt="Última evidencia de la unidad"
                          className="h-16 w-20 object-cover"
                        />
                      </a>
                    ) : (
                      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border ${
                        heroRecentAction.tone === 'cyan'
                          ? 'border-cyan-200 bg-cyan-50 text-cyan-700'
                          : heroRecentAction.tone === 'emerald'
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : heroRecentAction.tone === 'amber'
                              ? 'border-amber-200 bg-amber-50 text-amber-700'
                              : heroRecentAction.tone === 'rose'
                                ? 'border-rose-200 bg-rose-50 text-rose-700'
                                : 'border-slate-200 bg-slate-50 text-slate-700'
                      }`}>
                        <Icon path={heroRecentAction.iconPath} className="h-4.5 w-4.5" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.18em] text-slate-700">
                          Últimas acciones
                        </span>
                        <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                          {heroRecentAction.timeLabel}
                        </span>
                      </div>
                      <p className="mt-1.5 text-[13px] font-bold text-slate-900">{heroRecentAction.title}</p>
                      <p className="mt-1 text-[11px] leading-relaxed text-slate-600">{heroRecentAction.detail}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                        {heroRecentAction.destinationLabel ? (
                          <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-slate-600">
                            {heroRecentAction.destinationLabel}
                          </span>
                        ) : null}
                        {heroRecentAction.routeLabel ? (
                          <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-slate-600">
                            {heroRecentAction.routeLabel}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {!isUnitOpen && !monitorAction?.disabled ? (
                <div
                  className="relative col-span-2 inline-flex items-center justify-center gap-2 overflow-hidden rounded-full border px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.16em] animate-pulse lg:col-span-1 lg:self-end"
                  style={{
                    borderColor: withAlpha(tone.accentColor, 0.3),
                    background: `linear-gradient(180deg, ${withAlpha(tone.accentColor, 0.14)} 0%, ${withAlpha(tone.accentColor, 0.06)} 100%)`,
                    color: mixHexColor(tone.accentColor, '#0f172a', 0.18),
                    boxShadow: `0 10px 20px ${withAlpha(tone.accentColor, 0.14)}`
                  }}
                >
                  <span className="relative inline-flex h-2.5 w-2.5">
                    <span
                      className="absolute inline-flex h-full w-full rounded-full animate-ping"
                      style={{ backgroundColor: withAlpha(tone.accentColor, 0.45) }}
                    />
                    <span
                      className="relative inline-flex h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: tone.accentColor }}
                    />
                  </span>
                  <span>Ruta activa</span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className={`overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${isUnitOpen ? 'max-h-[1100px] opacity-100 pt-2.5' : 'max-h-0 opacity-0 pt-0'}`}>
          <div className="grid min-w-0 gap-2 pb-2.5 text-left sm:grid-cols-3">
            <div id={tutorialIds?.access} className="rounded-[18px] border border-slate-300/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.30),rgba(226,232,240,0.18))] px-3.5 py-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.62)]">
              <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-500">Acceso del chofer</p>
              <div className="mt-1.5 flex items-start justify-between gap-2">
                <p className="min-w-0 flex-1 break-all font-mono text-[10px] font-semibold text-slate-700">{accessLabel}</p>
                {(transport.driverAccessSlug || transport.slug) ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard?.writeText(accessLabel);
                    }}
                    className="shrink-0 rounded-full border border-slate-200 bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-600 hover:bg-slate-50"
                  >
                    Copiar
                  </button>
                ) : null}
              </div>
            </div>
            <button
              id={tutorialIds?.active}
              type="button"
              onClick={() => monitorAction?.onClick?.()}
              disabled={monitorAction?.disabled}
              className="relative overflow-hidden rounded-[18px] border border-slate-300/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.44),rgba(226,232,240,0.24))] px-3.5 py-2.5 text-left shadow-[0_14px_28px_rgba(15,23,42,0.08),inset_0_1px_0_rgba(255,255,255,0.74)] transition-all duration-300 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-45"
              style={
                monitorAction?.disabled
                  ? undefined
                  : {
                      borderColor: withAlpha('#059669', 0.42),
                      background: `linear-gradient(180deg, rgba(236,253,245,0.96) 0%, rgba(209,250,229,0.92) 52%, rgba(167,243,208,0.92) 100%)`,
                      boxShadow: `0 18px 34px rgba(5,150,105,0.18), inset 0 1px 0 rgba(255,255,255,0.86)`
                    }
              }
            >
              <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(140deg,rgba(255,255,255,0.24)_0%,transparent_36%)]" />
              <span className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 animate-[moduleShine_6.4s_ease-in-out_infinite]" style={{ background: `linear-gradient(90deg, transparent 0%, rgba(16,185,129,0.22) 50%, transparent 100%)`, transform: 'skewX(-18deg)' }} />
              {!monitorAction?.disabled ? (
                <span className="pointer-events-none absolute right-2 top-2 inline-flex h-2.5 w-2.5">
                  <span
                    className="absolute inline-flex h-full w-full animate-ping rounded-full"
                      style={{ backgroundColor: 'rgba(16,185,129,0.35)' }}
                  />
                  <span
                    className="relative inline-flex h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: '#059669' }}
                  />
                </span>
              ) : null}
              <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-emerald-800/80">Monitor</p>
              <p className="mt-1 text-[14px] font-bold tracking-[-0.03em] text-slate-950">
                {monitorAction?.disabled ? 'Sin ruta activa' : 'Monitor activo'}
              </p>
              {!monitorAction?.disabled ? (
                <p
                  className="mt-1 text-[10px] font-semibold uppercase tracking-[0.14em]"
                  style={{ color: '#047857' }}
                >
                  Entrar al seguimiento
                </p>
              ) : null}
            </button>
            <button
              id={tutorialIds?.history}
              type="button"
              onClick={() => historyAction?.onClick?.()}
              disabled={historyAction?.disabled}
              className="relative overflow-hidden rounded-[18px] border border-slate-300/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.44),rgba(226,232,240,0.24))] px-3.5 py-2.5 text-left shadow-[0_14px_28px_rgba(15,23,42,0.08),inset_0_1px_0_rgba(255,255,255,0.74)] transition-all duration-300 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-45"
            >
              <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(140deg,rgba(255,255,255,0.24)_0%,transparent_36%)]" />
              <span className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 animate-[moduleShine_7.1s_ease-in-out_infinite]" style={{ background: `linear-gradient(90deg, transparent 0%, ${withAlpha(tone.accentColor, 0.14)} 50%, transparent 100%)`, transform: 'skewX(-18deg)' }} />
              <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-500">Ver historial</p>
              <div className="mt-1.5 flex items-center gap-2">
                <span className="inline-flex h-2.5 w-2.5 rounded-full" style={{ backgroundColor: tone.accentColor, boxShadow: `0 0 0 4px ${withAlpha(tone.accentColor, 0.14)}` }} />
                <p className="text-[12px] font-semibold text-slate-800">Calendario y rutas</p>
              </div>
            </button>
          </div>

          {visibleRecentActions.length ? (
            <div className="mt-2 rounded-[20px] border border-slate-300/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.48),rgba(226,232,240,0.22))] p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.72)]">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-500">Últimas acciones</p>
                  <p className="mt-1 text-[12px] font-semibold text-slate-800">Foto, QR y código de barras de esta unidad</p>
                </div>
                <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-600">
                  {visibleRecentActions.length} evento{visibleRecentActions.length === 1 ? '' : 's'}
                </span>
              </div>

              <div className="mt-3 grid gap-2">
                {visibleRecentActions.map((event) => {
                  const actionMeta = buildTraceActionMeta(event);
                  const routeLabel = buildTransportDisplayName(event?._route || {});
                  const toneClass =
                    actionMeta.tone === 'cyan'
                      ? 'border-cyan-200 bg-cyan-50 text-cyan-700'
                      : actionMeta.tone === 'emerald'
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : actionMeta.tone === 'amber'
                          ? 'border-amber-200 bg-amber-50 text-amber-700'
                          : actionMeta.tone === 'rose'
                            ? 'border-rose-200 bg-rose-50 text-rose-700'
                            : 'border-slate-200 bg-slate-50 text-slate-700';

                  return (
                    <div key={event.id} className="flex items-start gap-3 rounded-[18px] border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
                      {event?.evidenceUrl ? (
                        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-sm">
                          <img src={String(event.evidenceUrl)} alt="Evidencia operativa" className="h-14 w-16 object-cover" />
                        </div>
                      ) : (
                        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border ${toneClass}`}>
                          <Icon path={actionMeta.iconPath} className="h-4 w-4" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.16em] ${toneClass}`}>
                            {actionMeta.badge}
                          </span>
                          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                            {formatActionDateTime(event.createdAt)}
                          </span>
                        </div>
                        <p className="mt-1 text-[13px] font-semibold text-slate-900">{actionMeta.title}</p>
                        <p className="mt-0.5 break-all text-xs leading-relaxed text-slate-600">{actionMeta.detail}</p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                          {event?.destinationName ? (
                            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-600">
                              {event.destinationName}
                            </span>
                          ) : null}
                          {routeLabel ? (
                            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-600">
                              {routeLabel}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>

        <div className={`overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${isDetailOpen ? 'max-h-[960px] opacity-100 pt-2.5' : 'max-h-0 opacity-0'}`}>
          <div
            className="rounded-[20px] border p-3 shadow-[0_16px_34px_rgba(15,23,42,0.10),inset_0_1px_0_rgba(255,255,255,0.7)]"
            style={{
              borderColor: withAlpha(tone.accentColor, 0.22),
              background: `linear-gradient(180deg, ${mixHexColor('#f8fbff', tone.accentColor, 0.12)} 0%, ${mixHexColor('#e8eef4', tone.accentColor, 0.24)} 100%)`
            }}
          >
            <TransportDetailSurface
              transport={transport}
              pricingInfo={pricingInfo}
              routeCount={routeCount}
              onEdit={onEdit}
              onOpenRouteSheet={onOpenRouteSheet}
              moduleActions={moduleActions}
              recentActions={recentActions}
              compact
            />
          </div>
        </div>
      </div>
    </article>
  );
};

const TransportModuleActions = ({ transport, tone, moduleActions = [], compact = false }) => {
  if (!moduleActions.length) return null;

  return (
    <div className={`rounded-[22px] border ${compact ? 'border-slate-300/85 bg-[linear-gradient(180deg,rgba(255,255,255,0.34),rgba(226,232,240,0.18))] p-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]' : 'border-white/10 bg-white/[0.045] p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-sm'}`}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <p className={`text-[9px] font-semibold uppercase tracking-[0.18em] ${compact ? 'text-slate-500' : 'text-slate-400'}`}>Módulos de unidad</p>
          <p className={`mt-0.5 text-[11px] font-medium ${compact ? 'text-slate-600' : 'text-slate-300/90'}`}>
            {compact ? 'Monitor, nueva hoja e historial desde esta misma unidad.' : 'Acceso directo al circuito operativo de esta unidad.'}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {moduleActions.map((action) => (
          <button
            key={action.id}
            type="button"
            onClick={action.onClick}
            disabled={action.disabled}
            className={`inline-flex ${compact ? 'min-h-[40px] flex-1 px-3 text-[10px]' : 'min-h-[46px] px-4 text-[11px]'} items-center justify-center gap-2 rounded-[16px] border font-black uppercase tracking-[0.14em] transition-all duration-300 ${
              action.disabled ? 'cursor-not-allowed opacity-45 saturate-75' : 'hover:-translate-y-0.5'
            }`}
            style={{
              background: action.primary
                ? `linear-gradient(180deg, ${withAlpha(action.accent || tone.accentColor, 0.98)} 0%, ${mixHexColor(action.accent || tone.accentColor, '#0f172a', 0.22)} 100%)`
                : `linear-gradient(180deg, ${mixHexColor('#f8fafc', action.accent || tone.accentColor, 0.08)} 0%, ${mixHexColor('#e2e8f0', action.accent || tone.accentColor, 0.16)} 100%)`,
              color: action.primary ? '#ffffff' : mixHexColor(action.accent || tone.accentColor, '#0f172a', 0.18),
              borderColor: withAlpha(action.accent || tone.accentColor, action.primary ? 0.14 : 0.18),
              boxShadow: action.primary
                ? `0 12px 24px ${withAlpha(action.accent || tone.accentColor, 0.18)}`
                : `0 10px 18px ${withAlpha(action.accent || tone.accentColor, 0.12)}`
            }}
          >
            <Icon path={action.iconPath} className="h-4 w-4" />
            <span>{action.label}</span>
          </button>
        ))}
      </div>
      {!compact ? (
        <p className="mt-2 text-[11px] font-medium text-slate-300/90">
          {getDriverDisplayName(transport)}: monitor activo, nueva hoja e historial dentro de su propio módulo.
        </p>
      ) : null}
    </div>
  );
};


const TransportistaModal = ({
  show,
  onClose,
  onSave,
  onDelete,
  editingTransport,
  showMessage,
  drivers = [],
  transportTypes,
  companyLiquidationSettings,
  reportsEnabled = false,
  planLabel = 'Plan Base',
  suggestedAlias = 'Unidad 1',
  companyId = DEFAULT_COMPANY_ID,
  usedIdentityColors = [],
  onStartTutorial,
  tutorialSeen = false,
  tutorialRepeatEnabled = true,
  onToggleTutorialRepeat
}) => {
  const isNew = !editingTransport;
  const [expandedSections, setExpandedSections] = useState({
    driver: true,
    transport: true,
    liquidation: false
  });

  const buildInitialFormData = React.useCallback(() => {
    const hasDriverData = Boolean(
      editingTransport?.currentDriverId ||
      editingTransport?.driverId ||
      editingTransport?.currentDriverName ||
      editingTransport?.driverName
    );
    const hasDriverAccess = Boolean(
      editingTransport?.driverAccessSlug ||
      editingTransport?.slug ||
      editingTransport?.password
    );
    const hasCustomLiquidation = Boolean(
      editingTransport?.liquidation?.mode === 'custom'
    );
    const hasLiquidationEnabled = Boolean(
      editingTransport?.liquidationEnabled ||
      editingTransport?.liquidation?.enabled ||
      editingTransport?.liquidationSettings?.enabled
    );
    const inferredTransportStatus =
      editingTransport?.transportStatus ||
      (editingTransport?.isActive === false ? 'inactive' : hasDriverData ? 'assigned' : 'available');

    return {
      name: editingTransport?.name || suggestedAlias,
      driverName: editingTransport?.currentDriverName || editingTransport?.driverName || '',
      driverLastName: editingTransport?.currentDriverLastName || editingTransport?.driverLastName || '',
      driverDni: editingTransport?.currentDriverDni || editingTransport?.driverDni || '',
      driverId: editingTransport?.currentDriverId || editingTransport?.driverId || '',
      selectedExistingDriverId: editingTransport?.currentDriverId || editingTransport?.driverId || '',
      transportType: editingTransport?.transportType || 'camioneta',
      slug: editingTransport?.driverAccessSlug || editingTransport?.slug || slugify(editingTransport?.currentDriverName || editingTransport?.driverName || suggestedAlias),
      password: editingTransport?.driverPassword || editingTransport?.password || generateRandomPassword(),
      patent: editingTransport?.patent || '',
      phone: editingTransport?.currentDriverPhone || editingTransport?.phone || '',
      transportModel: editingTransport?.transportModel || editingTransport?.vehicleLabel || '',
      photoURL: editingTransport?.currentDriverPhotoURL || editingTransport?.photoURL || '',
      photoStoragePath: editingTransport?.currentDriverPhotoStoragePath || editingTransport?.photoStoragePath || '',
      identityColor: editingTransport?.identityColor || '',
      currentDriverId: editingTransport?.currentDriverId || '',
      useAutomaticIdentityColor: !editingTransport?.identityColor,
      isActive: editingTransport?.isActive ?? true,
      liquidation: buildLiquidationFormState(editingTransport),
      includeDriver: isNew ? true : hasDriverData,
      includeTransport: true,
      enableDriverAccess: isNew ? true : hasDriverAccess,
      enableLiquidation: isNew ? reportsEnabled : hasLiquidationEnabled,
      linkDriverToTransport: isNew ? true : hasDriverData,
      driverStatus: editingTransport?.driverStatus || (hasDriverData ? 'assigned' : 'available'),
      transportStatus: inferredTransportStatus,
      liquidationTransportType: editingTransport?.liquidationTransportType || editingTransport?.transportType || 'camioneta'
    };
  }, [editingTransport, isNew, reportsEnabled, suggestedAlias]);

  const [formData, setFormData] = useState(buildInitialFormData);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const driverPhotoInputRef = useRef(null);

  useEffect(() => {
    setShowDeleteConfirm(false);
    setDeleteConfirmText('');
    setFormData(buildInitialFormData());
    setExpandedSections({
      driver: true,
      transport: true,
      liquidation: Boolean(editingTransport?.liquidation?.mode === 'custom') || (isNew && reportsEnabled)
    });
  }, [buildInitialFormData, editingTransport, isNew, reportsEnabled, show]);

  useEffect(() => {
    if (!show || !isNew || !tutorialRepeatEnabled || tutorialSeen || !onStartTutorial) return undefined;
    const timer = window.setTimeout(() => {
      onStartTutorial();
    }, 450);
    return () => window.clearTimeout(timer);
  }, [isNew, onStartTutorial, show, tutorialRepeatEnabled, tutorialSeen]);

  useEffect(() => {
    if (!formData.includeDriver) {
      setFormData((prev) => ({
        ...prev,
        enableDriverAccess: false,
        enableLiquidation: false,
        linkDriverToTransport: false
      }));
    }
  }, [formData.includeDriver]);

  useEffect(() => {
    if (!formData.includeTransport && isNew) {
      setFormData((prev) => ({
        ...prev,
        linkDriverToTransport: false
      }));
    }
  }, [formData.includeTransport, isNew]);

  const inheritedTypeConfig = getTypeLiquidationConfig(
    companyLiquidationSettings,
    formData.liquidationTransportType || formData.transportType
  );
  const liquidationLocked = !reportsEnabled;
  const driverAccessEnabled = formData.includeDriver && formData.enableDriverAccess;
  const wantsTransport = formData.includeTransport || !isNew;

  useEffect(() => {
    if (!isNew || !(formData.includeTransport || !isNew)) return;

    const desiredStatus = formData.includeDriver && formData.linkDriverToTransport ? 'assigned' : 'available';
    if (formData.transportStatus !== desiredStatus) {
      setFormData((prev) => ({
        ...prev,
        transportStatus: desiredStatus,
        isActive: desiredStatus !== 'inactive'
      }));
    }
  }, [formData.includeDriver, formData.includeTransport, formData.linkDriverToTransport, formData.transportStatus, isNew]);

  useEffect(() => {
    if (!formData.transportType) return;
    if (!formData.liquidationTransportType) {
      setFormData((prev) => ({
        ...prev,
        liquidationTransportType: prev.transportType || 'camioneta'
      }));
    }
  }, [formData.transportType, formData.liquidationTransportType]);
  const accessPreview =
    typeof window !== 'undefined' && driverAccessEnabled && formData.slug
      ? buildOperationalAccessUrl({ driverAccessSlug: formData.slug })
      : 'Acceso inactivo';
  const availableIdentityColors = useMemo(
    () =>
      IDENTITY_COLOR_OPTIONS.filter((color) =>
        usedIdentityColors.every((usedColor) => getColorDistance(color, usedColor) > 76)
      ),
    [usedIdentityColors]
  );

  const assignableDrivers = useMemo(
    () =>
      (drivers || []).filter((driver) => {
        const currentTransportId = driver?.currentTransportId || '';
        return !currentTransportId || currentTransportId === editingTransport?.id;
      }),
    [drivers, editingTransport?.id]
  );

  const selectedDriverRecord = useMemo(
    () => (drivers || []).find((driver) => driver.id === (formData.selectedExistingDriverId || formData.driverId || '')) || null,
    [drivers, formData.selectedExistingDriverId, formData.driverId]
  );

  const handleDriverPhotoFileChange = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    setIsUploadingPhoto(true);
    try {
      const photoPayload = await uploadCompressedProfilePhoto({
        file,
        companyId,
        entityType: 'drivers',
        entityId:
          formData.driverId ||
          formData.slug ||
          `${formData.driverName || 'chofer'}-${formData.driverLastName || ''}` ||
          'driver',
      });

      setFormData((prev) => ({
        ...prev,
        photoURL: photoPayload.photoURL,
        photoStoragePath: photoPayload.storagePath,
      }));

      showMessage(
        photoPayload.compressionApplied
          ? 'Foto 4x4 del chofer subida y comprimida.'
          : 'Foto 4x4 del chofer subida.',
        'success'
      );
    } catch (error) {
      showMessage(getProfilePhotoUploadErrorMessage(error), 'error');
    } finally {
      if (event?.target) {
        event.target.value = '';
      }
      setIsUploadingPhoto(false);
    }
  };

  const applyExistingDriverSelection = (driverId) => {
    const selectedDriver = (drivers || []).find((driver) => driver.id === driverId);

    if (!selectedDriver) {
      setFormData((prev) => ({
        ...prev,
        includeDriver: false,
        linkDriverToTransport: false,
        driverId: '',
        selectedExistingDriverId: '',
        currentDriverId: '',
        driverName: '',
        driverLastName: '',
        driverDni: '',
        phone: '',
        photoURL: '',
        photoStoragePath: '',
        slug: '',
        password: generateRandomPassword(),
        enableDriverAccess: false,
        driverStatus: 'available'
      }));
      return;
    }

    const accessSlug = slugify(selectedDriver.accessSlug || selectedDriver.driverAccessSlug || '');
    const selectedLiquidation = selectedDriver?.liquidationSettings?.config || null;
    const selectedLiquidationEnabled = !!selectedDriver?.liquidationSettings?.enabled;

    setFormData((prev) => ({
      ...prev,
      includeDriver: true,
      linkDriverToTransport: true,
      driverId: selectedDriver.id,
      selectedExistingDriverId: selectedDriver.id,
      currentDriverId: selectedDriver.id,
      driverName: selectedDriver.firstName || selectedDriver.driverName || '',
      driverLastName: selectedDriver.lastName || selectedDriver.driverLastName || '',
      driverDni: selectedDriver.dni || selectedDriver.driverDni || '',
      phone: selectedDriver.phone || '',
      photoURL: selectedDriver.photoURL || '',
      photoStoragePath: selectedDriver.photoStoragePath || '',
      slug: accessSlug,
      password: selectedDriver.password || selectedDriver.driverPassword || prev.password || generateRandomPassword(),
      enableDriverAccess: Boolean(accessSlug),
      driverStatus: selectedDriver.status || selectedDriver.driverStatus || 'assigned',
      enableLiquidation: selectedLiquidationEnabled,
      liquidation: selectedLiquidation ? buildLiquidationFormState({ liquidation: selectedLiquidation }) : prev.liquidation,
      liquidationTransportType: selectedDriver?.liquidationSettings?.transportType || prev.liquidationTransportType || prev.transportType || 'camioneta'
    }));
  };

  const previewTransport = {
    ...formData,
    currentDriverId: formData.driverId || formData.selectedExistingDriverId || formData.currentDriverId,
    currentDriverName: formData.driverName,
    currentDriverLastName: formData.driverLastName,
    currentDriverPhone: formData.phone,
    currentDriverPhotoURL: formData.photoURL,
    currentDriverDni: formData.driverDni,
    driverAccessSlug: driverAccessEnabled ? formData.slug : ''
  };

  const autoGenerateSlug = () => {
    const base =
      [formData.driverName, formData.driverLastName].filter(Boolean).join(' ') ||
      formData.name ||
      formData.transportType;
    setFormData((prev) => ({ ...prev, slug: slugify(base) }));
  };

  const handleCustomLiquidationChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      liquidation: {
        ...prev.liquidation,
        customSettings: {
          ...prev.liquidation.customSettings,
          [field]: value
        }
      }
    }));
  };

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleDelete = async () => {
    if (!editingTransport?.id) return;

    if (deleteConfirmText.trim().toUpperCase() !== 'ELIMINAR') {
      showMessage('Escribí ELIMINAR para confirmar el borrado de la ficha.', 'error');
      return;
    }

    const deleted = await onDelete?.(editingTransport);
    if (deleted) {
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
      onClose();
    }
  };

  const handleSave = () => {
    const wantsDriver = !!formData.includeDriver;
    const wantsTransportNow = !!wantsTransport;

    if (!wantsDriver && !wantsTransportNow) {
      showMessage('Activá al menos chofer o transporte para guardar este registro.', 'error');
      return;
    }

    if (wantsDriver && !String(formData.driverName || '').trim()) {
      showMessage('Cargá al menos el nombre del chofer para guardar la ficha.', 'error');
      return;
    }

    if (wantsTransportNow && !String(formData.name || '').trim()) {
      showMessage('El alias de la unidad es obligatorio.', 'error');
      return;
    }

    if (wantsTransportNow && !String(formData.transportType || '').trim()) {
      showMessage('Elegí un tipo de transporte para continuar.', 'error');
      return;
    }

    if (driverAccessEnabled && !String(formData.slug || '').trim()) {
      showMessage('Definí un acceso operativo para el chofer.', 'error');
      return;
    }

    if (driverAccessEnabled && !String(formData.password || '').trim()) {
      showMessage('Cargá una clave para el acceso operativo.', 'error');
      return;
    }

    if (formData.enableLiquidation && !wantsDriver) {
      showMessage('La liquidación solo se puede guardar si también existe el chofer.', 'error');
      return;
    }

    if (
      formData.enableLiquidation &&
      formData.liquidation?.mode === 'custom' &&
      Number(formData.liquidation?.customSettings?.pricePerKm || 0) <= 0
    ) {
      showMessage('Si usás tarifa propia, cargá un precio por km mayor a cero.', 'error');
      return;
    }

    onSave(formData, isNew);
    onClose();
  };

  if (!show) return null;

  const chipBase = 'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold';
  const sectionWrapper = 'rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_12px_28px_rgba(15,23,42,0.05)]';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-[6px]">
      <div
        id="unit-modal-shell"
        className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-[32px] border border-white/60 bg-white/96 p-6 shadow-[0_32px_70px_rgba(15,23,42,0.28)] md:p-7"
      >
        <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
              {isNew ? 'Nuevo registro' : 'Edición operativa'}
            </p>
            <h3 className="mt-1 text-2xl font-bold text-slate-900">
              {isNew ? 'Nuevo registro logístico' : 'Editar registro logístico'}
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              Creá chofer, transporte y liquidación desde un mismo espacio, juntos o por separado.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onStartTutorial}
              className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-[14px] border border-cyan-200 bg-cyan-50 px-4 text-sm font-semibold text-cyan-800 transition hover:bg-cyan-100"
            >
              <Icon path="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" className="h-4 w-4" />
              Tutorial
            </button>
            <button
              type="button"
              onClick={onToggleTutorialRepeat}
              className={`inline-flex min-h-[40px] items-center justify-center gap-2 rounded-[14px] border px-4 text-sm font-semibold transition ${
                tutorialRepeatEnabled
                  ? 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-800 hover:bg-fuchsia-100'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span className={`inline-flex h-2.5 w-2.5 rounded-full ${tutorialRepeatEnabled ? 'bg-fuchsia-500' : 'bg-slate-300'}`} />
              Repetir {tutorialRepeatEnabled ? 'On' : 'Off'}
            </button>
          </div>
        </div>

        <div
          id="unit-modal-identity"
          className="mb-5 rounded-[24px] border border-slate-200 bg-[linear-gradient(180deg,#f8fafc_0%,#eef2f7_100%)] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.75)]"
        >
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <TransportAvatar transport={previewTransport} size="lg" />
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                  Vista previa
                </p>
                <h4 className="mt-1 text-xl font-bold text-slate-900">
                  {wantsTransport ? formData.name || 'Transporte en preparación' : buildDriverDisplayName(previewTransport, 'Chofer en preparación')}
                </h4>
                <p className="mt-1 text-sm text-slate-500">
                  {buildDriverDisplayName(previewTransport, 'Sin chofer cargado')} · {getTransportTypeLabel(formData.transportType)}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className={`${chipBase} ${formData.includeDriver ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-100 text-slate-500'}`}>
                <span className={`h-2 w-2 rounded-full ${formData.includeDriver ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                Chofer
              </span>
              <span className={`${chipBase} ${wantsTransport ? 'border-sky-200 bg-sky-50 text-sky-700' : 'border-slate-200 bg-slate-100 text-slate-500'}`}>
                <span className={`h-2 w-2 rounded-full ${wantsTransport ? 'bg-sky-500' : 'bg-slate-300'}`} />
                Transporte
              </span>
              <span className={`${chipBase} ${formData.enableLiquidation ? 'border-violet-200 bg-violet-50 text-violet-700' : 'border-slate-200 bg-slate-100 text-slate-500'}`}>
                <span className={`h-2 w-2 rounded-full ${formData.enableLiquidation ? 'bg-violet-500' : 'bg-slate-300'}`} />
                Liquidación
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <section id="unit-modal-driver-section" className={sectionWrapper}>
            <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 md:flex-row md:items-center md:justify-between">
              <button
                type="button"
                onClick={() => toggleSection('driver')}
                className="flex min-w-0 items-center gap-3 text-left"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-[16px] bg-emerald-50 text-emerald-700">
                  <Icon path="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.964 0a9 9 0 10-11.964 0m11.964 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z" className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-lg font-bold text-slate-900">Chofer</p>
                  <p className="text-sm text-slate-500">Identidad operativa, acceso y disponibilidad.</p>
                  {isNew ? <p className="mt-1 text-xs font-medium text-slate-400">Podés crear el chofer ahora o dejar este bloque apagado para más adelante.</p> : null}
                </div>
              </button>
              <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                  <input
                    type="checkbox"
                    checked={!!formData.includeDriver}
                    onChange={(e) => setFormData((prev) => ({ ...prev, includeDriver: e.target.checked }))}
                    className="h-4 w-4 rounded border-slate-300 text-emerald-600"
                  />
                  Incluir chofer
                </label>
                <button
                  type="button"
                  onClick={() => toggleSection('driver')}
                  className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600"
                >
                  {expandedSections.driver ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
            </div>

            {expandedSections.driver ? (
              formData.includeDriver ? (
                isNew ? (
                  <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <input
                      type="text"
                      placeholder="Nombre del chofer"
                      value={formData.driverName}
                      onChange={(e) => setFormData((prev) => ({ ...prev, driverName: e.target.value }))}
                      className="w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2.5 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Apellido"
                      value={formData.driverLastName}
                      onChange={(e) => setFormData((prev) => ({ ...prev, driverLastName: e.target.value }))}
                      className="w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2.5 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="DNI"
                      value={formData.driverDni}
                      onChange={(e) => setFormData((prev) => ({ ...prev, driverDni: e.target.value }))}
                      className="w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2.5 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Teléfono"
                      value={formData.phone}
                      onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                      className="w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2.5 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Foto 4x4 del chofer (URL opcional)"
                      value={formData.photoURL}
                      onChange={(e) => setFormData((prev) => ({ ...prev, photoURL: e.target.value }))}
                      className="w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2.5 text-sm md:col-span-2"
                    />
                    <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-4 md:col-span-2">
                      <input
                        ref={driverPhotoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleDriverPhotoFileChange}
                      />
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div className="flex items-center gap-3">
                  <DriverAvatar
                    photoURL={formData.photoURL}
                    displayName={[formData.driverName, formData.driverLastName].filter(Boolean).join(' ') || 'Chofer'}
                    sizeClass="h-16 w-16"
                  />
                          <div>
                            <p className="text-sm font-semibold text-slate-900">Foto 4x4 del chofer</p>
                            <p className="text-xs text-slate-500">
                              Se comprime antes de subir para no cargar peso de más.
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => driverPhotoInputRef.current?.click()}
                          disabled={isUploadingPhoto}
                          className="inline-flex min-h-[44px] items-center justify-center rounded-[14px] border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 disabled:opacity-60"
                        >
                          {isUploadingPhoto ? 'Subiendo foto...' : formData.photoURL ? 'Cambiar foto' : 'Subir foto'}
                        </button>
                      </div>
                    </div>
                    <div id="unit-modal-driver-access" className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3 md:col-span-2">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">Acceso operativo</p>
                          <p className="text-sm text-slate-500">El acceso queda listo para cuando el chofer tenga una ruta activa asignada.</p>
                        </div>
                        <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700">
                          <input
                            type="checkbox"
                            checked={!!formData.enableDriverAccess}
                            onChange={(e) => setFormData((prev) => ({ ...prev, enableDriverAccess: e.target.checked }))}
                            className="h-4 w-4 rounded border-slate-300 text-cyan-600"
                          />
                          Activar acceso operativo
                        </label>
                      </div>

                      {formData.enableDriverAccess ? (
                        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
                          <div className="space-y-3">
                            <input
                              type="text"
                              placeholder="Identificador del acceso del chofer"
                              value={formData.slug}
                              onChange={(e) => setFormData((prev) => ({ ...prev, slug: slugify(e.target.value) }))}
                              className="w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2.5 text-sm"
                            />
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
                              <input
                                type="text"
                                placeholder="Clave de acceso"
                                value={formData.password}
                                onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                                className="w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2.5 text-sm"
                              />
                              <button
                                type="button"
                                onClick={() => setFormData((prev) => ({ ...prev, password: generateRandomPassword() }))}
                                className="inline-flex min-h-[44px] items-center justify-center rounded-[14px] border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700"
                              >
                                Generar clave
                              </button>
                            </div>
                          </div>
                          <div className="rounded-[18px] border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Vista previa</p>
                            <p className="mt-2 break-all font-mono text-[12px] text-slate-800">{accessPreview}</p>
                            <button
                              type="button"
                              onClick={autoGenerateSlug}
                              className="mt-3 inline-flex min-h-[38px] items-center justify-center rounded-[12px] border border-cyan-200 bg-cyan-50 px-3 text-xs font-semibold text-cyan-800"
                            >
                              Generar acceso
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="mt-3 text-sm text-slate-500">
                          El chofer queda cargado sin acceso individual. Podés habilitarlo más adelante.
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">Estado del chofer</label>
                      <select
                        value={formData.driverStatus}
                        onChange={(e) => setFormData((prev) => ({ ...prev, driverStatus: e.target.value }))}
                        className="w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2.5 text-sm"
                      >
                        <option value="available">Disponible</option>
                        <option value="assigned">Asignado</option>
                        <option value="in_route">En ruta</option>
                        <option value="inactive">Inactivo</option>
                      </select>
                    </div>

                    {wantsTransport ? (
                      <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3 md:col-span-2">
                        <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
                          <input
                            type="checkbox"
                            checked={!!formData.linkDriverToTransport}
                            onChange={(e) => setFormData((prev) => ({ ...prev, linkDriverToTransport: e.target.checked }))}
                            className="h-4 w-4 rounded border-slate-300 text-emerald-600"
                          />
                          Asignar este chofer a la unidad en este mismo guardado
                        </label>
                        <p className="mt-2 text-sm text-slate-500">
                          Si lo desactivás, chofer y transporte nacen separados y quedan listos para vincularse después.
                        </p>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="mt-4 space-y-4">
                    <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">
                            {selectedDriverRecord ? buildDriverDisplayName(selectedDriverRecord, 'Chofer asignado') : 'Unidad sin chofer asignado'}
                          </p>
                          <p className="mt-1 text-sm text-slate-500">
                            {selectedDriverRecord
                              ? `Acceso ${selectedDriverRecord.accessSlug || selectedDriverRecord.driverAccessSlug ? 'activo' : 'inactivo'} · ${selectedDriverRecord.phone || 'Sin teléfono'}`
                              : 'Esta unidad puede quedar libre o vincularse a un chofer ya existente.'}
                          </p>
                        </div>
                        {formData.includeDriver ? (
                          <button
                            type="button"
                            onClick={() => applyExistingDriverSelection('')}
                            className="inline-flex min-h-[40px] items-center justify-center rounded-[12px] border border-rose-200 bg-white px-4 text-sm font-semibold text-rose-700"
                          >
                            Desvincular chofer
                          </button>
                        ) : null}
                      </div>
                    </div>

                    <div className="rounded-[18px] border border-slate-200 bg-white px-4 py-4">
                      <input
                        ref={driverPhotoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleDriverPhotoFileChange}
                      />
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div className="flex items-center gap-3">
                          <DriverAvatar
                            photoURL={formData.photoURL || selectedDriverRecord?.photoURL || ''}
                            displayName={
                              [formData.driverName, formData.driverLastName].filter(Boolean).join(' ') ||
                              buildDriverDisplayName(selectedDriverRecord, 'Chofer')
                            }
                            sizeClass="h-20 w-20"
                          />
                          <div>
                            <p className="text-sm font-semibold text-slate-900">Foto 4x4 del chofer</p>
                            <p className="text-xs text-slate-500">
                              Podés cargarla ahora aunque el chofer sea anterior. La imagen se comprime antes de subir.
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => driverPhotoInputRef.current?.click()}
                          disabled={isUploadingPhoto || !formData.selectedExistingDriverId}
                          className="inline-flex min-h-[44px] items-center justify-center rounded-[14px] border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isUploadingPhoto ? 'Subiendo foto...' : formData.photoURL || selectedDriverRecord?.photoURL ? 'Cambiar foto' : 'Subir foto'}
                        </button>
                      </div>
                      {!formData.selectedExistingDriverId ? (
                        <p className="mt-3 text-xs text-slate-500">
                          Primero elegí un chofer para poder cargarle o actualizarle la foto.
                        </p>
                      ) : null}
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">Asignar o cambiar chofer</label>
                      <select
                        value={formData.selectedExistingDriverId || ''}
                        onChange={(e) => applyExistingDriverSelection(e.target.value)}
                        className="w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2.5 text-sm"
                      >
                        <option value="">Sin chofer asignado</option>
                        {assignableDrivers.map((driver) => (
                          <option key={driver.id} value={driver.id}>
                            {buildDriverDisplayName(driver, 'Chofer sin nombre')}
                            {driver.currentTransportId && driver.currentTransportId === editingTransport?.id
                              ? ' · Asignado a esta unidad'
                              : ''}
                          </option>
                        ))}
                      </select>
                      <p className="mt-2 text-sm text-slate-500">
                        Desde acá podés asignar, cambiar o liberar el vínculo del chofer. La ficha de la unidad se actualiza en toda la operación.
                      </p>
                    </div>

                    {selectedDriverRecord ? (
                      <div className="grid grid-cols-1 gap-3 rounded-[18px] border border-slate-200 bg-white px-4 py-3 md:grid-cols-4">
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Documento</p>
                          <p className="mt-1 text-sm font-semibold text-slate-900">{selectedDriverRecord.dni || 'Sin DNI'}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Teléfono</p>
                          <p className="mt-1 text-sm font-semibold text-slate-900">{selectedDriverRecord.phone || 'Sin teléfono'}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Acceso</p>
                          <p className="mt-1 text-sm font-semibold text-slate-900">
                            {selectedDriverRecord.accessSlug || selectedDriverRecord.driverAccessSlug ? 'Activo' : 'Sin acceso'}
                          </p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Clave</p>
                          <p className="mt-1 break-all font-mono text-sm font-semibold text-slate-900">{selectedDriverRecord.password || selectedDriverRecord.driverPassword || 'Sin clave'}</p>
                        </div>
                      </div>
                    ) : null}

                    <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">Acceso operativo del chofer</p>
                          <p className="text-sm text-slate-500">Podés corregir la ruta y la clave sin recrear la unidad.</p>
                        </div>
                        <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700">
                          <input
                            type="checkbox"
                            checked={!!formData.enableDriverAccess}
                            onChange={(e) => setFormData((prev) => ({ ...prev, enableDriverAccess: e.target.checked }))}
                            className="h-4 w-4 rounded border-slate-300 text-cyan-600"
                          />
                          Activar acceso operativo
                        </label>
                      </div>

                      {formData.enableDriverAccess ? (
                        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
                          <div className="space-y-3">
                            <input
                              type="text"
                              placeholder="Ruta del chofer"
                              value={formData.slug}
                              onChange={(e) => setFormData((prev) => ({ ...prev, slug: slugify(e.target.value) }))}
                              className="w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2.5 text-sm"
                            />
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
                              <input
                                type="text"
                                placeholder="Clave de acceso"
                                value={formData.password}
                                onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                                className="w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2.5 text-sm"
                              />
                              <button
                                type="button"
                                onClick={() => setFormData((prev) => ({ ...prev, password: generateRandomPassword() }))}
                                className="inline-flex min-h-[44px] items-center justify-center rounded-[14px] border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700"
                              >
                                Generar clave
                              </button>
                            </div>
                          </div>
                          <div className="rounded-[18px] border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Vista previa</p>
                            <p className="mt-2 break-all font-mono text-[12px] text-slate-800">{accessPreview}</p>
                            <button
                              type="button"
                              onClick={autoGenerateSlug}
                              className="mt-3 inline-flex min-h-[38px] items-center justify-center rounded-[12px] border border-cyan-200 bg-cyan-50 px-3 text-xs font-semibold text-cyan-800"
                            >
                              Regenerar ruta
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="mt-3 text-sm text-slate-500">El chofer queda sin acceso individual hasta que vuelvas a activarlo.</p>
                      )}
                    </div>
                  </div>
                )
              ) : (
                <p className="mt-4 text-sm text-slate-500">
                  {isNew
                    ? 'Podés guardar este registro sin chofer. La unidad quedará viva y lista para recibir uno más adelante.'
                    : 'Esta unidad está libre. Podés asignarle un chofer desde este mismo bloque cuando lo necesites.'}
                </p>
              )
            ) : null}
          </section>

          <section id="unit-modal-transport-section" className={sectionWrapper}>
            <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 md:flex-row md:items-center md:justify-between">
              <button
                type="button"
                onClick={() => toggleSection('transport')}
                className="flex min-w-0 items-center gap-3 text-left"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-[16px] bg-sky-50 text-sky-700">
                  <Icon path={getTransportIconPath(formData.transportType)} className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-lg font-bold text-slate-900">Transporte</p>
                  <p className="text-sm text-slate-500">Unidad operativa, identidad y estado general.</p>
                  {isNew ? <p className="mt-1 text-xs font-medium text-slate-400">La unidad puede nacer sola o quedar vinculada al chofer en este mismo guardado.</p> : null}
                </div>
              </button>
              <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                  <input
                    type="checkbox"
                    checked={!!wantsTransport}
                    disabled={!isNew}
                    onChange={(e) => setFormData((prev) => ({ ...prev, includeTransport: e.target.checked }))}
                    className="h-4 w-4 rounded border-slate-300 text-sky-600"
                  />
                  Incluir transporte
                </label>
                <button
                  type="button"
                  onClick={() => toggleSection('transport')}
                  className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600"
                >
                  {expandedSections.transport ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
            </div>

            {expandedSections.transport ? (
              wantsTransport ? (
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Alias de unidad</label>
                    <input
                      type="text"
                      placeholder="Unidad 4"
                      value={formData.name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                      className="w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Tipo de transporte</label>
                    <select
                      value={formData.transportType}
                      onChange={(e) => setFormData((prev) => ({ ...prev, transportType: e.target.value, liquidationTransportType: e.target.value }))}
                      className="w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800"
                    >
                      {transportTypes.map((type) => (
                        <option key={`transport-${type.value}`} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Patente</label>
                    <input
                      type="text"
                      placeholder="AB123CD"
                      value={formData.patent}
                      onChange={(e) => setFormData((prev) => ({ ...prev, patent: e.target.value.toUpperCase() }))}
                      className="w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Modelo del transporte <span className="text-slate-400">opcional</span></label>
                    <input
                      type="text"
                      placeholder="Sprinter 311 CDI / Wave 110 / Kangoo Express"
                      value={formData.transportModel}
                      onChange={(e) => setFormData((prev) => ({ ...prev, transportModel: e.target.value }))}
                      className="w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Estado de la unidad</label>
                    <select
                      value={formData.transportStatus}
                      onChange={(e) => setFormData((prev) => ({ ...prev, transportStatus: e.target.value, isActive: e.target.value !== 'inactive' }))}
                      className="w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800"
                    >
                      {(isNew ? TRANSPORT_STATUS_OPTIONS.filter((option) => ['available', 'assigned'].includes(option.value)) : TRANSPORT_STATUS_OPTIONS).map((option) => (
                        <option key={`transport-status-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <p className="mt-2 text-xs text-slate-500">
                      Al crear, la unidad puede quedar disponible o asignada. Los demás estados se usan después en operación.
                    </p>
                  </div>

                  <div className="rounded-[20px] border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Identidad visual</p>
                        <p className="text-sm text-slate-500">La unidad mantiene su color aunque cambie de chofer.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, useAutomaticIdentityColor: !prev.useAutomaticIdentityColor }))}
                        className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"
                      >
                        {formData.useAutomaticIdentityColor ? 'Automático' : 'Manual'}
                      </button>
                    </div>

                    {!formData.useAutomaticIdentityColor ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(availableIdentityColors.length ? availableIdentityColors : IDENTITY_COLOR_OPTIONS).map((color) => (
                          <button
                            key={color}
                            type="button"
                            aria-label={`Seleccionar color ${color}`}
                            onClick={() => setFormData((prev) => ({ ...prev, identityColor: color }))}
                            className={`h-8 w-8 rounded-full border-2 transition ${formData.identityColor === color ? 'scale-110 border-slate-950 shadow-[0_0_0_4px_rgba(15,23,42,0.08)]' : 'border-white/90'}`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="mt-3 text-sm text-slate-500">
                        El sistema toma un color coherente según el tipo y el tablero actual.
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="mt-4 text-sm text-slate-500">
                  Podés guardar solo el chofer y dejar el transporte para más adelante.
                </p>
              )
            ) : null}
          </section>

          <section id="unit-modal-liquidation-section" className={sectionWrapper}>
            <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 md:flex-row md:items-center md:justify-between">
              <button
                type="button"
                onClick={() => toggleSection('liquidation')}
                className="flex min-w-0 items-center gap-3 text-left"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-[16px] bg-violet-50 text-violet-700">
                  <Icon path="M12 8.25c-2.07 0-3.75 1.51-3.75 3.375S9.93 15 12 15s3.75-1.51 3.75-3.375S14.07 8.25 12 8.25zm0-5.25v3m0 12v3m8.25-8.25h-3m-12 0h-3m14.47 5.22l-2.12-2.12m-8.7 0l-2.12 2.12m12.94-10.56l-2.12 2.12m-8.7 0L3.28 7.41" className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-lg font-bold text-slate-900">Liquidación</p>
                  <p className="text-sm text-slate-500">Condición económica del chofer según el tipo de transporte.</p>
                  {isNew ? <p className="mt-1 text-xs font-medium text-slate-400">Si esta parte todavía no tiene voz oficial, el tutorial la acompaña con carteles.</p> : null}
                </div>
              </button>
              <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                  <input
                    type="checkbox"
                    checked={!!formData.enableLiquidation}
                    disabled={liquidationLocked || !formData.includeDriver}
                    onChange={(e) => setFormData((prev) => ({ ...prev, enableLiquidation: e.target.checked }))}
                    className="h-4 w-4 rounded border-slate-300 text-violet-600"
                  />
                  Activar liquidación
                </label>
                <button
                  type="button"
                  onClick={() => toggleSection('liquidation')}
                  className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600"
                >
                  {expandedSections.liquidation ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
            </div>

            {expandedSections.liquidation ? (
              <div className="mt-4 space-y-4">
                {liquidationLocked ? (
                  <div className="rounded-[18px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    Tu plan actual no habilita esta configuración. Se activa con {planLabel} y se administra desde Reportes.
                  </div>
                ) : null}

                {!formData.includeDriver ? (
                  <p className="text-sm text-slate-500">
                    Primero activá el bloque de chofer para poder definir una liquidación.
                  </p>
                ) : !formData.enableLiquidation ? (
                  <p className="text-sm text-slate-500">
                    El chofer puede quedar cargado sin liquidación. Podés activarla cuando la operación ya esté definida.
                  </p>
                ) : (
                  <>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Tipo de transporte para liquidar</label>
                        <select
                          value={formData.liquidationTransportType}
                          onChange={(e) => setFormData((prev) => ({ ...prev, liquidationTransportType: e.target.value }))}
                          className="w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2.5 text-sm"
                        >
                          {transportTypes.map((type) => (
                            <option key={`liquidation-${type.value}`} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className={`space-y-3 ${liquidationLocked ? 'opacity-55' : ''}`}>
                      <label className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="liquidationMode"
                          checked={formData.liquidation.mode === 'inherit'}
                          disabled={liquidationLocked}
                          onChange={() =>
                            setFormData((prev) => ({
                              ...prev,
                              liquidation: {
                                ...prev.liquidation,
                                mode: 'inherit'
                              }
                            }))
                          }
                        />
                        <div>
                          <p className="font-semibold text-slate-800">Heredar tarifa del tipo</p>
                          <p className="text-sm text-slate-500">
                            {inheritedTypeConfig
                              ? inheritedTypeConfig.costModel === 'byScale'
                                ? `El tipo ${formData.liquidationTransportType} usa escala de distancia.`
                                : `El tipo ${formData.liquidationTransportType} tiene $ ${formatCurrency(inheritedTypeConfig.pricePerKm || 0)} / km.`
                              : `Todavía no hay una tarifa por tipo cargada para ${formData.liquidationTransportType}.`}
                          </p>
                        </div>
                      </label>

                      <label className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="liquidationMode"
                          checked={formData.liquidation.mode === 'custom'}
                          disabled={liquidationLocked}
                          onChange={() =>
                            setFormData((prev) => ({
                              ...prev,
                              liquidation: {
                                ...prev.liquidation,
                                mode: 'custom'
                              }
                            }))
                          }
                        />
                        <div>
                          <p className="font-semibold text-slate-800">Usar tarifa propia</p>
                          <p className="text-sm text-slate-500">
                            Esta condición queda asociada al chofer y toma como referencia el tipo seleccionado.
                          </p>
                        </div>
                      </label>
                    </div>

                    {!liquidationLocked && formData.liquidation.mode === 'custom' ? (
                      <div className="grid grid-cols-1 gap-3 border-t border-slate-100 pt-4 md:grid-cols-3">
                        <div>
                          <label className="mb-2 block text-sm font-medium text-slate-700">Precio por km</label>
                          <input
                            type="number"
                            value={formData.liquidation.customSettings.pricePerKm}
                            onChange={(e) => handleCustomLiquidationChange('pricePerKm', e.target.value)}
                            className="w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2.5 text-sm"
                          />
                        </div>
                        <div>
                          <label className="mb-2 block text-sm font-medium text-slate-700">Cargo base por ruta</label>
                          <input
                            type="number"
                            value={formData.liquidation.customSettings.baseRouteFee}
                            onChange={(e) => handleCustomLiquidationChange('baseRouteFee', e.target.value)}
                            className="w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2.5 text-sm"
                          />
                        </div>
                        <div>
                          <label className="mb-2 block text-sm font-medium text-slate-700">Mínimo por ruta</label>
                          <input
                            type="number"
                            value={formData.liquidation.customSettings.minimumRouteFee}
                            onChange={(e) => handleCustomLiquidationChange('minimumRouteFee', e.target.value)}
                            className="w-full rounded-[14px] border border-slate-200 bg-white px-3 py-2.5 text-sm"
                          />
                        </div>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}
          </section>
        </div>

        <div className="mt-6 space-y-4">
          {!isNew ? (
            <div className="rounded-[24px] border border-rose-200/80 bg-rose-50/80 p-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="max-w-xl">
                  <p className="text-sm font-bold text-rose-700">Eliminar ficha</p>
                  <p className="mt-1 text-sm text-rose-700/80">
                    Esta acción borra la unidad del tablero. El resto de registros operativos no se tocan.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteConfirm((prev) => !prev);
                    setDeleteConfirmText('');
                  }}
                  className={`inline-flex min-h-[42px] items-center justify-center rounded-[14px] border px-4 text-sm font-semibold transition-colors ${
                    showDeleteConfirm
                      ? 'border-rose-300 bg-white text-rose-700'
                      : 'border-rose-300 bg-rose-600 text-white hover:bg-rose-700'
                  }`}
                >
                  {showDeleteConfirm ? 'Cancelar borrado' : 'Eliminar ficha'}
                </button>
              </div>

              {showDeleteConfirm ? (
                <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-rose-700/80">
                      Escribí ELIMINAR para confirmar
                    </label>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="ELIMINAR"
                      className="mt-2 w-full rounded-[14px] border border-rose-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-rose-400"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleteConfirmText.trim().toUpperCase() !== 'ELIMINAR'}
                    className="inline-flex min-h-[44px] items-center justify-center rounded-[14px] border border-rose-300 bg-rose-700 px-4 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Confirmar eliminación
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              onClick={onClose}
              className="inline-flex min-h-[46px] items-center justify-center rounded-[16px] bg-slate-200 px-4 text-sm font-semibold text-slate-800"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              className="inline-flex min-h-[46px] items-center justify-center rounded-[16px] bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm"
            >
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// Componente principal para el dashboard de logística
const LogisticsDashboard = ({
  onSelectTransport,
  onBackToMenu,
  onStartTutorial,
  tutorialSeen = false,
  tutorialActive = false,
  tutorialFocusTarget = null,
  tutorialTransitionRequest = null,
  onStartUnitModalTutorial,
  unitModalTutorialSeen = false,
  unitModalTutorialRepeatEnabled = true,
  onToggleUnitModalTutorialRepeat,
  routes,
  showMessage,
  companyId = DEFAULT_COMPANY_ID,
  companyName = 'Empresa Base',
  companyModules = null,
  companyPlanSettings = null,
  commercialState = null,
  onGiaContextChange
}) => {
  const [transports, setTransports] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTransport, setEditingTransport] = useState(null);
  const [companyLiquidationSettings, setCompanyLiquidationSettings] = useState(null);
  const [expandedTransportId, setExpandedTransportId] = useState(null);
  const [detailTransportId, setDetailTransportId] = useState(null);
  const [showDemoUnit, setShowDemoUnit] = useState(false);
  const [demoExpanded, setDemoExpanded] = useState(false);
  const [demoDetailOpen, setDemoDetailOpen] = useState(false);
  const nextUnitNumber = useMemo(() => {
    const explicitNumbers = transports
      .map((transport) => Number(transport?.unitNumber))
      .filter((value) => Number.isFinite(value) && value > 0);

    if (explicitNumbers.length) {
      return Math.max(...explicitNumbers) + 1;
    }

    return Math.max(transports.length, 0) + 1;
  }, [transports]);

  const suggestedUnitAlias = useMemo(() => `Unidad ${nextUnitNumber}`, [nextUnitNumber]);
  const unitsSectionRef = React.useRef(null);
  const driversSectionRef = React.useRef(null);
  const hasAutoCollapsedUnitsRef = React.useRef(false);
  const [fleetExpenses, setFleetExpenses] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [showDriversBoard, setShowDriversBoard] = useState(false);
  const [expandedDriverCardId, setExpandedDriverCardId] = useState('');
  const [serviceContracts, setServiceContracts] = useState([]);
  const [scanEvents, setScanEvents] = useState([]);

  const useLegacyFallback = useMemo(
    () => shouldUseLegacyTransportFallback(companyId),
    [companyId]
  );
  const planMeta = useMemo(
    () =>
      resolveCompanyPlanMeta({
        planSettings: companyPlanSettings,
        modulesSettings: companyModules
      }),
    [companyModules, companyPlanSettings]
  );
  const reportsEnabled = companyModules?.reports?.enabled !== false && !commercialState?.shouldRestrictOperations;
  const contractClientsByTransportId = useMemo(() => {
    const result = new Map();

    serviceContracts.forEach((contract) => {
      const transportId = String(contract?.fixedTransportId || '').trim();
      if (!transportId) return;
      if (String(contract?.transportAssignmentMode || '').trim().toLowerCase() !== 'fixed') return;
      if (String(contract?.status || 'active').trim().toLowerCase() === 'closed') return;

      const current = result.get(transportId) || [];
      const clientName = String(contract?.clientNameSnapshot || contract?.name || '').trim() || 'Cliente';
      if (!current.includes(clientName)) {
        current.push(clientName);
      }
      result.set(transportId, current);
    });

    return result;
  }, [serviceContracts]);

  const transportConfig = {
    camioneta: {
      iconPath: 'M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25',
      colorClasses: {
        bg: 'bg-gradient-to-br from-green-500 to-emerald-600',
        iconBg: 'bg-green-200/95',
        iconText: 'text-green-700',
        titleText: 'text-white',
        descriptionText: 'text-green-100/90',
        fieldBg: 'bg-emerald-50/95 border-emerald-200/80',
        fieldLabel: 'text-emerald-700/80',
        fieldValue: 'text-slate-900',
        fieldAccent: 'text-emerald-700'
      },
    },
    moto: {
      iconPath: 'M15.75 10.5l4.724-4.724A3.375 3.375 0 0015.75 3h-2.25a3.375 3.375 0 00-3.375 3.375v2.25M9.375 10.5H5.25A2.25 2.25 0 003 12.75v4.248M9.375 10.5h-.75V8.25m-.75 0a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm9 0a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM7.5 15.75m-3 0a3 3 0 116 0 3 3 0 01-6 0zm-3 0h7.5',
      colorClasses: {
        bg: 'bg-gradient-to-br from-orange-500 to-amber-600',
        iconBg: 'bg-orange-200/95',
        iconText: 'text-orange-700',
        titleText: 'text-white',
        descriptionText: 'text-orange-100/90',
        fieldBg: 'bg-amber-50/95 border-amber-200/80',
        fieldLabel: 'text-amber-700/80',
        fieldValue: 'text-slate-900',
        fieldAccent: 'text-amber-700'
      },
    },
    auto: {
      iconPath: 'M3.75 16.5v-1.125c0-.621.504-1.125 1.125-1.125h14.25c.621 0 1.125.504 1.125 1.125V16.5M4.5 12l1.32-3.301A2.25 2.25 0 017.909 7.5h8.182a2.25 2.25 0 012.089 1.199L19.5 12M6.75 16.5h.008v.008H6.75V16.5zm10.5 0h.008v.008h-.008V16.5z',
      colorClasses: {
        bg: 'bg-gradient-to-br from-sky-500 to-cyan-600',
        iconBg: 'bg-sky-200/95',
        iconText: 'text-sky-700',
        titleText: 'text-white',
        descriptionText: 'text-sky-100/90',
        fieldBg: 'bg-cyan-50/95 border-cyan-200/80',
        fieldLabel: 'text-cyan-700/80',
        fieldValue: 'text-slate-900',
        fieldAccent: 'text-cyan-700'
      },
    },
    camion: {
      iconPath: 'M1.5 13.5h12m0 0V6.75A2.25 2.25 0 0011.25 4.5h-6A2.25 2.25 0 003 6.75v6.75m10.5 0h3.879a1.5 1.5 0 001.06-.44l1.621-1.62a1.5 1.5 0 00.44-1.061V9.75a1.5 1.5 0 00-.44-1.06l-1.62-1.621a1.5 1.5 0 00-1.061-.44H13.5m-9 10.5a1.5 1.5 0 103 0 1.5 1.5 0 00-3 0zm12 0a1.5 1.5 0 103 0 1.5 1.5 0 00-3 0z',
      colorClasses: {
        bg: 'bg-gradient-to-br from-indigo-500 to-violet-600',
        iconBg: 'bg-indigo-200/95',
        iconText: 'text-indigo-700',
        titleText: 'text-white',
        descriptionText: 'text-indigo-100/90',
        fieldBg: 'bg-violet-50/95 border-violet-200/80',
        fieldLabel: 'text-violet-700/80',
        fieldValue: 'text-slate-900',
        fieldAccent: 'text-violet-700'
      },
    }
  };

  useEffect(() => {
    if (!db || !companyId) return;

    const liquidationRef = doc(db, resolveCompanyLiquidationSettingsPath(companyId));

    const unsubscribe = onSnapshot(
      liquidationRef,
      (docSnap) => {
        setCompanyLiquidationSettings(docSnap.exists() ? docSnap.data() : null);
      },
      (err) => {
        console.error('Error fetching company liquidation settings:', err);
        setCompanyLiquidationSettings(null);
      }
    );

    return () => unsubscribe();
  }, [companyId]);

  useEffect(() => {
    if (!db || !companyId) return undefined;

    const driversRef = collection(db, resolveDriversCollectionPath(companyId));
    const unsubscribe = onSnapshot(
      driversRef,
      (snapshot) => {
        setDrivers(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      },
      (error) => {
        console.error('Error fetching company drivers:', error);
        setDrivers([]);
      }
    );

    return () => unsubscribe();
  }, [companyId]);

  useEffect(() => {
    if (!db || !companyId) return undefined;

    const eventsRef = query(collection(db, resolveScanEventsCollectionPath(companyId)));
    const unsubscribe = onSnapshot(
      eventsRef,
      (snapshot) => {
        const events = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .sort((a, b) => getEventTimestampMillis(b.createdAt) - getEventTimestampMillis(a.createdAt))
          .slice(0, 180);
        setScanEvents(events);
      },
      (error) => {
        console.error('Error fetching scan events for logistics dashboard:', error);
        setScanEvents([]);
      }
    );

    return () => unsubscribe();
  }, [companyId]);

  useEffect(() => {
    if (!db || !companyId) return undefined;

    const expensesRef = collection(db, resolveFleetExpensesCollectionPath(companyId));
    const unsubscribe = onSnapshot(
      expensesRef,
      (snapshot) => {
        setFleetExpenses(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      },
      (error) => {
        console.error('Error fetching company fleet expenses:', error);
        setFleetExpenses([]);
      }
    );

    return () => unsubscribe();
  }, [companyId]);

  useEffect(() => {
    if (!db || !companyId) return undefined;

    const contractsRef = collection(db, resolveServiceContractsCollectionPath(companyId));
    const unsubscribe = onSnapshot(
      contractsRef,
      (snapshot) => {
        setServiceContracts(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      },
      (error) => {
        console.error('Error fetching service contracts for logistics dashboard:', error);
        setServiceContracts([]);
      }
    );

    return () => unsubscribe();
  }, [companyId]);

  useEffect(() => {
    if (!drivers.length) {
      setSelectedDriverId('');
      return;
    }

    setSelectedDriverId((current) => {
      if (current && drivers.some((driver) => driver.id === current)) {
        return current;
      }
      return drivers[0]?.id || '';
    });
  }, [drivers]);

  const driversById = useMemo(() => {
    const map = new Map();
    drivers.forEach((driver) => {
      map.set(String(driver?.id || '').trim(), driver);
    });
    return map;
  }, [drivers]);

  useEffect(() => {
    if (!db || !appId || !companyId) return;

    let companyDocs = [];
    let legacyDocs = [];
    let companyReady = false;
    let legacyReady = !useLegacyFallback;
    let isCancelled = false;

    const companyTransportsRef = collection(db, resolveTransportsCollectionPath(companyId));

    const syncTransports = () => {
      if (!companyReady || !legacyReady || isCancelled) return;

      const mergedDocs = useLegacyFallback
        ? mergeTransportSources(companyDocs, legacyDocs)
        : companyDocs;

      setTransports(sortTransports(mergedDocs));
      setLoading(false);
    };

    const unsubscribeCompany = onSnapshot(
      companyTransportsRef,
      (snapshot) => {
        companyReady = true;
        companyDocs = snapshot.docs.map((docSnap) => {
          const rawTransport = {
            id: docSnap.id,
            _source: 'company',
            ...docSnap.data()
          };
          const identity = resolveTransportIdentity(rawTransport);
          return {
            ...rawTransport,
            currentDriverName: rawTransport.currentDriverName || rawTransport.driverName || '',
            currentDriverLastName: rawTransport.currentDriverLastName || rawTransport.driverLastName || '',
            currentDriverPhone: rawTransport.currentDriverPhone || rawTransport.phone || '',
            currentDriverPhotoURL: rawTransport.currentDriverPhotoURL || rawTransport.photoURL || '',
            currentDriverDni: rawTransport.currentDriverDni || rawTransport.driverDni || '',
            driverAccessSlug: rawTransport.driverAccessSlug || rawTransport.slug || '',
            transportModel: rawTransport.transportModel || rawTransport.vehicleLabel || '',
            identityColor: rawTransport.identityColor || identity.identityColor,
            identitySoftColor: rawTransport.identitySoftColor || identity.identitySoftColor
          };
        });
        syncTransports();
      },
      (err) => {
        companyReady = true;
        companyDocs = [];
        console.error('Error fetching company transports:', err);
        syncTransports();
      }
    );

    let unsubscribeLegacy = () => {};

    if (useLegacyFallback) {
      const legacyTransportsRef = collection(db, `artifacts/${appId}/public/data/transports`);

      unsubscribeLegacy = onSnapshot(
        legacyTransportsRef,
        (snapshot) => {
          legacyReady = true;
          legacyDocs = snapshot.docs.map((docSnap) => {
            const rawTransport = {
              id: docSnap.id,
              _source: 'legacy',
              ...docSnap.data()
            };
            const identity = resolveTransportIdentity(rawTransport);
            return {
              ...rawTransport,
              currentDriverName: rawTransport.currentDriverName || rawTransport.driverName || '',
              currentDriverLastName: rawTransport.currentDriverLastName || rawTransport.driverLastName || '',
              currentDriverPhone: rawTransport.currentDriverPhone || rawTransport.phone || '',
              currentDriverPhotoURL: rawTransport.currentDriverPhotoURL || rawTransport.photoURL || '',
              currentDriverDni: rawTransport.currentDriverDni || rawTransport.driverDni || '',
              driverAccessSlug: rawTransport.driverAccessSlug || rawTransport.slug || '',
              identityColor: rawTransport.identityColor || identity.identityColor,
              identitySoftColor: rawTransport.identitySoftColor || identity.identitySoftColor
            };
          });
          syncTransports();
        },
        (err) => {
          legacyReady = true;
          legacyDocs = [];
          console.error('Error fetching legacy transports:', err);
          syncTransports();
        }
      );
    }

    return () => {
      isCancelled = true;
      unsubscribeCompany();
      unsubscribeLegacy();
    };
  }, [companyId, appId, useLegacyFallback]);

  const hydratedTransports = useMemo(() => {
    const nextTransports = transports.map((transport) => {
      const linkedDriver =
        driversById.get(String(transport?.currentDriverId || '').trim()) ||
        driversById.get(String(transport?.driverId || '').trim()) ||
        null;

      if (!linkedDriver) return transport;

      return {
        ...transport,
        currentDriverName:
          transport?.currentDriverName ||
          transport?.driverName ||
          linkedDriver?.firstName ||
          linkedDriver?.driverName ||
          '',
        currentDriverLastName:
          transport?.currentDriverLastName ||
          transport?.driverLastName ||
          linkedDriver?.lastName ||
          linkedDriver?.driverLastName ||
          '',
        currentDriverPhone:
          transport?.currentDriverPhone ||
          transport?.phone ||
          linkedDriver?.phone ||
          linkedDriver?.driverPhone ||
          '',
        currentDriverDni:
          transport?.currentDriverDni ||
          transport?.driverDni ||
          linkedDriver?.dni ||
          linkedDriver?.driverDni ||
          '',
        currentDriverPhotoURL:
          transport?.currentDriverPhotoURL ||
          transport?.photoURL ||
          linkedDriver?.photoURL ||
          linkedDriver?.driverPhotoURL ||
          '',
        driverAccessSlug:
          transport?.driverAccessSlug ||
          transport?.slug ||
          linkedDriver?.accessSlug ||
          linkedDriver?.driverAccessSlug ||
          '',
      };
    });

    return sortTransports(nextTransports);
  }, [driversById, transports]);

  useEffect(() => {
    if (expandedTransportId && !hydratedTransports.some((transport) => transport.id === expandedTransportId)) {
      setExpandedTransportId(null);
    }
    if (detailTransportId && !hydratedTransports.some((transport) => transport.id === detailTransportId)) {
      setDetailTransportId(null);
    }
  }, [detailTransportId, expandedTransportId, hydratedTransports]);

  useEffect(() => {
    if (tutorialActive && transports.length) {
      setExpandedTransportId('__all__');
      return undefined;
    }

    if (!transports.length || hasAutoCollapsedUnitsRef.current) return undefined;

    hasAutoCollapsedUnitsRef.current = true;
    const transportIds = transports.map((transport) => transport.id).filter(Boolean);
    if (!transportIds.length) return undefined;
    setExpandedTransportId('__all__');

    const collapseTimer = setTimeout(() => {
      setExpandedTransportId(null);
    }, 1000);

    return () => clearTimeout(collapseTimer);
  }, [transports, tutorialActive]);

  useEffect(() => {
    if (!tutorialActive || !tutorialFocusTarget || !transports.length) return;

    const firstTransportId = transports[0]?.id;
    if (!firstTransportId) return;

    const shouldExpandFirstUnit = [
      '#logistics-first-unit',
      '#logistics-first-unit-header',
      '#logistics-first-unit-create',
      '#logistics-first-unit-active',
      '#logistics-first-unit-history',
      '#logistics-first-unit-access',
      '#logistics-first-unit-detail'
    ].includes(tutorialFocusTarget);

    if (shouldExpandFirstUnit) {
      setExpandedTransportId(firstTransportId);
    }

    const targetNode = document.querySelector(tutorialFocusTarget);
    if (targetNode?.scrollIntoView) {
      targetNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [tutorialActive, tutorialFocusTarget, transports]);

  useEffect(() => {
    if (transports.length > 0) {
      setShowDemoUnit(false);
      setDemoExpanded(false);
      setDemoDetailOpen(false);
    }
  }, [transports.length]);

  useEffect(() => {
    if (tutorialActive && transports.length === 0) {
      setShowDemoUnit(true);
      setDemoExpanded(true);
    }
  }, [tutorialActive, transports.length]);

  useEffect(() => {
    if (tutorialTransitionRequest !== 'routeCreator') return;
    if (!transports.length) return;

    const firstTransport = transports[0];
    if (!firstTransport) return;

    onSelectTransport({
      ...firstTransport,
      companyId,
      companyName,
      companyModules,
      companyPlanSettings,
      commercialState,
      initialLogisticsView: 'create',
    });
  }, [
    commercialState,
    companyId,
    companyModules,
    companyName,
    companyPlanSettings,
    onSelectTransport,
    transports,
    tutorialTransitionRequest,
  ]);

  const handleToggleUnit = (transportId) => {
    setExpandedTransportId((current) => (current === transportId ? null : transportId));
  };

  const handleToggleDetail = (transportId) => {
    setDetailTransportId((current) => (current === transportId ? null : transportId));
  };

  const countRoutes = (transport) => {
    return routes.filter((r) => {
      if (!isRouteOperationallyActive(r)) return false;

      if (r.companyId && companyId && r.companyId !== companyId) {
        return false;
      }

      if (r.transporterId && transport.id) {
        return r.transporterId === transport.id;
      }

      if (r.transporterSlug && transport.slug) {
        return r.transporterSlug === transport.slug;
      }

      return (
        !r.transporterId &&
        !r.transporterSlug &&
        (
          r.transport === transport.transportType ||
          r.transportType === transport.transportType
        )
      );
    }).length;
  };

  const activeRoutesCount = routes.filter((route) =>
    isRouteOperationallyActive(route)
  ).length;

  const activeUnitsCount = transports.filter((transport) => transport.isActive !== false).length;

  const companyRoutes = useMemo(
    () =>
      (routes || []).filter((route) => {
        if (!companyId) return true;
        return !route?.companyId || route.companyId === companyId;
      }),
    [companyId, routes]
  );

  const routeMatchesTransport = useCallback((route, transport) => {
    if (!route || !transport) return false;

    if (route?.companyId && companyId && route.companyId !== companyId) {
      return false;
    }

    if (route?.transporterId && transport?.id) {
      return route.transporterId === transport.id;
    }

    if (route?.transporterSlug && transport?.slug) {
      return route.transporterSlug === transport.slug;
    }

    if (route?.driverId && transport?.currentDriverId) {
      return route.driverId === transport.currentDriverId;
    }

    return (
      !route?.transporterId &&
      !route?.transporterSlug &&
      (route?.transport === transport?.transportType || route?.transportType === transport?.transportType)
    );
  }, [companyId]);

  const recentActionsByTransportId = useMemo(() => {
    const routeById = new Map();
    companyRoutes.forEach((route) => {
      routeById.set(String(route?.id || '').trim(), route);
    });

    const actionsByTransportId = new Map();

    hydratedTransports.forEach((transport) => {
      const relatedRouteIds = new Set(
        companyRoutes
          .filter((route) => routeMatchesTransport(route, transport))
          .map((route) => String(route?.id || '').trim())
          .filter(Boolean)
      );

      if (!relatedRouteIds.size) {
        actionsByTransportId.set(String(transport?.id || '').trim(), []);
        return;
      }

      const transportActions = scanEvents
        .filter((event) => {
          const eventTransportId = String(event?.transportIdSnapshot || '').trim();
          const eventDriverId = String(event?.driverIdSnapshot || '').trim();
          const transportId = String(transport?.id || '').trim();
          const driverId = String(transport?.currentDriverId || '').trim();

          if (eventTransportId && transportId && eventTransportId === transportId) return true;
          if (eventDriverId && driverId && eventDriverId === driverId) return true;
          return relatedRouteIds.has(String(event?.routeId || '').trim());
        })
        .map((event) => ({
          ...event,
          _route: routeById.get(String(event?.routeId || '').trim()) || null,
          _routeLabel:
            buildTransportDisplayName(routeById.get(String(event?.routeId || '').trim()) || {}, '') ||
            String(event?.transportNameSnapshot || '').trim(),
        }))
        .sort((a, b) => getEventTimestampMillis(b.createdAt) - getEventTimestampMillis(a.createdAt))
        .slice(0, 5);

      actionsByTransportId.set(String(transport?.id || '').trim(), transportActions);
    });

    return actionsByTransportId;
  }, [companyRoutes, hydratedTransports, routeMatchesTransport, scanEvents]);

  const transportTypeLabelMap = useMemo(
    () =>
      TRANSPORT_TYPE_OPTIONS.reduce((acc, item) => {
        acc[item.value] = item.label;
        return acc;
      }, {}),
    []
  );

  const driverModuleData = useMemo(() => {
    const normalizeName = (value = '') =>
      String(value || '')
        .trim()
        .toLowerCase();

    const resolveRouteStatus = (route) => {
      return getRouteStatus(route);
    };

    const buildMonthBucket = (value) => {
      const date = value?.toDate ? value.toDate() : new Date(value);
      if (Number.isNaN(date.getTime())) return null;
      return {
        key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        label: date.toLocaleDateString('es-AR', { month: 'short', year: 'numeric' })
      };
    };

    const transportByDriverId = new Map();
    transports.forEach((transport) => {
      if (transport?.currentDriverId) {
        transportByDriverId.set(transport.currentDriverId, transport);
      }
    });

    const activeRouteByDriverId = new Map();
    companyRoutes.forEach((route) => {
      if (isRouteOperationallyActive(route) && route?.driverId) {
        activeRouteByDriverId.set(route.driverId, route);
      }
    });

    const getRouteMatchesDriver = (route, driver) => {
      if (driver?.id && route?.driverId) {
        return route.driverId === driver.id;
      }
      const routeName = normalizeName(buildDriverDisplayName(route, ''));
      const driverName = normalizeName(buildDriverDisplayName(driver, ''));
      return Boolean(routeName && driverName && routeName === driverName);
    };

    const getExpenseMatchesDriver = (expense, driver) => {
      if (driver?.id && expense?.driverId) {
        return expense.driverId === driver.id;
      }
      const expenseName = normalizeName(expense?.driverName || '');
      const driverName = normalizeName(buildDriverDisplayName(driver, ''));
      return Boolean(expenseName && driverName && expenseName === driverName);
    };

    const driverRecords = drivers.map((driver) => {
      const displayName = buildDriverDisplayName(driver, 'Chofer sin nombre');
      const assignedTransport =
        transportByDriverId.get(driver.id) ||
        transports.find((transport) => transport?.currentDriverId === driver.id) ||
        null;
      const driverRoutes = companyRoutes.filter((route) => getRouteMatchesDriver(route, driver));
      const driverExpenses = fleetExpenses.filter(
        (expense) => Boolean(expense?.impactDriver) && getExpenseMatchesDriver(expense, driver)
      );

      const activeRoute =
        activeRouteByDriverId.get(driver.id) ||
        driverRoutes.find((route) => isRouteOperationallyActive(route)) ||
        null;

      const statusTone =
        driver?.isActive === false
          ? 'inactivo'
          : activeRoute
            ? 'en_ruta'
            : assignedTransport
              ? 'asignado'
              : 'disponible';

      const statusLabel = DRIVER_STATUS_TONES[statusTone]?.label || 'Disponible';
      const totalKm = driverRoutes.reduce(
        (sum, route) => sum + Number(getRouteTotalDistance(route, {}) || 0),
        0
      );
      const completedDestinations = driverRoutes.reduce(
        (sum, route) =>
          sum +
          (route?.destinations || []).filter((destination) => destination?.status === 'completed').length,
        0
      );
      const incompleteDestinations = driverRoutes.reduce(
        (sum, route) =>
          sum +
          (route?.destinations || []).filter((destination) => destination?.status === 'incomplete').length,
        0
      );
      const cancelledDestinations = driverRoutes.reduce(
        (sum, route) =>
          sum +
          (route?.destinations || []).filter((destination) => destination?.status === 'cancelled').length,
        0
      );
      const totalEarnings = driverRoutes.reduce(
        (sum, route) =>
          sum +
          Number(
            calculateRouteCost({
              route,
              settings: companyLiquidationSettings || undefined
            }).totalCost || 0
          ),
        0
      );

      const monthlyMap = driverRoutes.reduce((acc, route) => {
        const bucket = buildMonthBucket(route?.date || route?.createdAt);
        if (!bucket) return acc;
        const currentBucket = acc[bucket.key] || { label: bucket.label, amount: 0 };
        currentBucket.amount =
          currentBucket.amount +
          Number(
            calculateRouteCost({
              route,
              settings: companyLiquidationSettings || undefined
            }).totalCost || 0
          );
        acc[bucket.key] = currentBucket;
        return acc;
      }, {});

      const monthlyEarnings = Object.entries(monthlyMap)
        .map(([key, payload]) => ({ key, label: payload.label, amount: payload.amount }))
        .sort((a, b) => b.key.localeCompare(a.key))
        .slice(0, 6);

      const transportTypeLabels = Array.from(
        new Set(
          [
            ...driverRoutes.map((route) => route?.transportType || route?.transport || ''),
            assignedTransport?.transportType || ''
          ].filter(Boolean)
        )
      ).map((type) => transportTypeLabelMap[type] || String(type || '').replace(/^./, (letter) => letter.toUpperCase()));

      const multas = driverExpenses.filter((expense) => expense?.type === 'multas');
      const incidentes = driverExpenses.filter((expense) => expense?.type === 'incidentes');

      const firstSeenValue = driver?.createdAt || driverRoutes[0]?.date || '';
      const currentTransportName = assignedTransport
        ? buildTransportDisplayName(assignedTransport)
        : activeRoute
          ? buildTransportDisplayName(activeRoute)
          : 'Sin unidad asignada';
      const currentTransportMeta = assignedTransport
        ? buildTransportMeta(assignedTransport)
        : activeRoute
          ? buildTransportMeta({
              transportModel: activeRoute?.transportModel,
              transportType: activeRoute?.transportType || activeRoute?.transport,
              patent: activeRoute?.transportPatent
            })
          : 'Disponible para vincular';
      const accessPath = buildOperationalAccessUrl({
        driverAccessSlug: driver?.accessSlug || driver?.driverAccessSlug || assignedTransport?.driverAccessSlug || assignedTransport?.slug || ''
      });

      const sortedDriverRoutes = [...driverRoutes].sort(
        (a, b) => new Date(`${b?.date || '1970-01-01'}T00:00:00`) - new Date(`${a?.date || '1970-01-01'}T00:00:00`)
      );

      const mapRouteToHistoryItem = (route) => {
        const routeStatus = resolveRouteStatus(route);
        const destinations = route?.destinations || [];
        return {
          id: route.id,
          name: route?.name || `Ruta ${route?.date || ''}`.trim(),
          dateLabel: formatShortDate(route?.date),
          transportId: route?.transporterId || route?.transportId || '',
          transportLabel: buildTransportDisplayName({
            transportName: route?.transporterName,
            transportModel: route?.transportModel,
            transportType: route?.transportType || route?.transport
          }),
          statusLabel:
            routeStatus === 'in_progress'
              ? 'En ruta'
              : routeStatus === 'pending'
                ? 'Pendiente'
                : routeStatus === 'programmed'
                  ? 'Programada'
                  : routeStatus === 'cancelled'
                    ? 'Cancelada'
                    : 'Completada',
          kmLabel: `${Number(getRouteTotalDistance(route, {}) || 0).toFixed(1)} km`,
          destinationsLabel: `${destinations.length} destinos`
        };
      };

      const recentRoutes = sortedDriverRoutes.slice(0, 3).map(mapRouteToHistoryItem);
      const routeHistory = sortedDriverRoutes.map(mapRouteToHistoryItem);

      return {
        id: driver.id,
        displayName,
        photoURL: driver?.photoURL || '',
        statusTone,
        statusLabel,
        accessSummary: accessPath ? `Acceso operativo listo · ${accessPath}` : 'Acceso pendiente de activación',
        accessPath,
        accessPassword: driver?.password || driver?.driverPassword || assignedTransport?.driverPassword || assignedTransport?.password || '',
        accessPasswordSummary: (driver?.password || driver?.driverPassword || assignedTransport?.driverPassword || assignedTransport?.password || '') ? `Clave · ${driver?.password || driver?.driverPassword || assignedTransport?.driverPassword || assignedTransport?.password || ''}` : 'Clave pendiente',
        currentTransportId: assignedTransport?.id || '',
        currentTransportName,
        currentTransportMeta,
        totalRoutes: driverRoutes.length,
        totalKm,
        totalKmLabel: `${Number(totalKm || 0).toFixed(1)} km`,
        completedDestinations,
        incompleteDestinations,
        cancelledDestinations,
        totalEarnings,
        monthlyEarnings,
        transportTypeLabels,
        transportTypeCount: transportTypeLabels.length,
        multasCount: multas.length,
        multasAmount: multas.reduce((sum, item) => sum + Number(item?.amount || 0), 0),
        incidentesCount: incidentes.length,
        incidentesAmount: incidentes.reduce((sum, item) => sum + Number(item?.amount || 0), 0),
        firstSeenLabel: formatShortDate(firstSeenValue),
        recentRoutes,
        routeHistory,
        recentRoutesCount: routeHistory.length,
        summaryLine: activeRoute ? 'Tiene una operación viva ahora mismo.' : assignedTransport ? 'Está vinculado a una unidad activa.' : 'Todavía no tiene una unidad asignada.',
        kpis: [
          { label: 'Recorridos', value: String(driverRoutes.length), helper: 'Rutas registradas en historial' },
          { label: 'Kilómetros', value: `${Number(totalKm || 0).toFixed(1)} km`, helper: 'Distancia acumulada en recorridos' },
          { label: 'Destinos completos', value: String(completedDestinations), helper: 'Entregas finalizadas correctamente' },
          { label: 'Cancelados / incompletos', value: `${cancelledDestinations} / ${incompleteDestinations}`, helper: 'Incidencias de cierre sobre destinos' }
        ]
      };
    });

    const sortedDriverCards = [...driverRecords].sort((a, b) => {
      const toneOrder = { en_ruta: 0, asignado: 1, disponible: 2, inactivo: 3 };
      if (toneOrder[a.statusTone] !== toneOrder[b.statusTone]) {
        return toneOrder[a.statusTone] - toneOrder[b.statusTone];
      }
      return a.displayName.localeCompare(b.displayName, 'es');
    });

    const selectedRecord =
      sortedDriverCards.find((driver) => driver.id === selectedDriverId) ||
      sortedDriverCards[0] ||
      null;

    return {
      driverCards: sortedDriverCards,
      selectedDriverReport: selectedRecord
    };
  }, [companyLiquidationSettings, companyRoutes, drivers, fleetExpenses, selectedDriverId, transports, transportTypeLabelMap]);

  const focusDriversBoard = (driverId = '') => {
    const resolvedDriverId = driverId || selectedDriverId || driverModuleData.driverCards[0]?.id || '';
    setShowDriversBoard(true);
    if (resolvedDriverId) {
      setSelectedDriverId(resolvedDriverId);
      setExpandedDriverCardId(resolvedDriverId);
    }

    window.requestAnimationFrame(() => {
      driversSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleToggleDriverBoard = () => {
    if (showDriversBoard) {
      setShowDriversBoard(false);
      return;
    }
    focusDriversBoard();
  };

  useEffect(() => {
    if (typeof onGiaContextChange !== 'function') return;

    const focusedTransport =
      transports.find((transport) => transport.id === detailTransportId) ||
      transports.find((transport) => transport.id === expandedTransportId) ||
      null;

    const selectedDriverName =
      driverModuleData.selectedDriverReport?.displayName ||
      driverModuleData.selectedDriverReport?.driverName ||
      '';

    let subsectionLabel = 'Choferes y unidades';
    let focusLabel = '';

    if (showDriversBoard) {
      subsectionLabel = 'Choferes';
      focusLabel = selectedDriverName;
    } else if (detailTransportId && focusedTransport) {
      subsectionLabel = 'Detalle de unidad';
      focusLabel = focusedTransport.name || focusedTransport.alias || focusedTransport.id || '';
    } else if (expandedTransportId === '__all__') {
      subsectionLabel = 'Base operativa desplegada';
    } else if (expandedTransportId && focusedTransport) {
      subsectionLabel = 'Unidad desplegada';
      focusLabel = focusedTransport.name || focusedTransport.alias || focusedTransport.id || '';
    }

    onGiaContextChange({ subsectionLabel, focusLabel });
  }, [
    detailTransportId,
    driverModuleData.selectedDriverReport,
    expandedTransportId,
    onGiaContextChange,
    showDriversBoard,
    transports,
  ]);

  const handleToggleDriverCard = (driverId) => {
    setSelectedDriverId(driverId);
    setExpandedDriverCardId((current) => (current === driverId ? '' : driverId));
  };

  const handleCopyDriverAccess = (accessPath) => {
    if (!accessPath) {
      showMessage?.('Este chofer todavía no tiene acceso operativo listo.', 'info');
      return;
    }

    navigator.clipboard?.writeText(accessPath);
    showMessage?.('Acceso completo del chofer copiado.', 'success');
  };

  const handleOpenDriverRouteSheet = (transportId) => {
    const transport = transports.find((item) => item.id === transportId);
    if (!transport) {
      showMessage?.('El chofer todavía no tiene una unidad operativa para abrir una ruta.', 'info');
      return;
    }

    handleOpenRouteSheet(transport, { initialLogisticsView: 'create' });
  };

  const handleOpenDriverRouteMonitor = (routeId, transportId) => {
    if (!routeId) {
      showMessage?.('No se encontró la ruta que querés abrir.', 'info');
      return;
    }

    const transport = transports.find((item) => item.id === transportId)
      || transports.find((item) => getCurrentOperationalRouteForTransport(item)?.id === routeId)
      || null;

    if (!transport) {
      showMessage?.('No se encontró la unidad vinculada a esa ruta histórica.', 'info');
      return;
    }

    handleOpenRouteSheet(transport, {
      initialRouteEntry: {
        routeId,
        companyId
      }
    });
  };

  const handleEditTransportista = (transportId) => {
    const transport = transports.find((t) => t.id === transportId);
    if (transport) {
      setEditingTransport(transport);
      setShowModal(true);
    }
  };


  const getSaveSuccessMessage = ({ isNewRecord, shouldCreateDriver, shouldCreateTransport, shouldLinkDriverToTransport, newDriverId, previousDriverId, previousLiquidationEnabled, currentLiquidationEnabled, formData }) => {
    if (isNewRecord) {
      if (shouldCreateDriver && shouldCreateTransport) {
        return shouldLinkDriverToTransport
          ? 'Chofer y transporte quedaron listos y vinculados.'
          : 'Chofer y transporte quedaron creados como registros separados.';
      }
      if (shouldCreateDriver) return 'Chofer guardado y listo para asignar.';
      if (shouldCreateTransport) return 'Transporte guardado y listo para operar.';
      return 'Registro guardado con éxito.';
    }

    const messages = [];

    if (previousDriverId !== (shouldLinkDriverToTransport ? newDriverId : '')) {
      if (previousDriverId && !(shouldLinkDriverToTransport ? newDriverId : '')) {
        messages.push('Chofer desvinculado');
      } else if (!previousDriverId && (shouldLinkDriverToTransport ? newDriverId : '')) {
        messages.push('Chofer asignado a la unidad');
      } else if (previousDriverId && (shouldLinkDriverToTransport ? newDriverId : '') && previousDriverId !== newDriverId) {
        messages.push('Chofer reasignado');
      }
    }

    if (previousLiquidationEnabled !== currentLiquidationEnabled) {
      messages.push(currentLiquidationEnabled ? 'Liquidación activada' : 'Liquidación desactivada');
    } else if (currentLiquidationEnabled && editingTransport?.liquidationTransportType !== (formData.liquidationTransportType || formData.transportType || '')) {
      messages.push('Liquidación actualizada');
    }

    if ((editingTransport?.transportModel || editingTransport?.vehicleLabel || '') !== ((formData.transportModel || '').trim())) {
      messages.push('Modelo del transporte actualizado');
    }

    if ((editingTransport?.name || '').trim() !== (formData.name || '').trim()) {
      messages.push('Alias de la unidad actualizado');
    }

    if (!messages.length) {
      return 'Cambios guardados en la unidad.';
    }

    return `${messages.slice(0, 2).join(' · ')}.`;
  };

  const handleSaveTransportista = async (formData, isNew) => {
    const shouldCreateDriver = !!formData.includeDriver;
    const shouldCreateTransport = !!formData.includeTransport || !isNew;
    const shouldEnableDriverAccess = shouldCreateDriver && !!formData.enableDriverAccess;
    const shouldEnableLiquidation = shouldCreateDriver && !!formData.enableLiquidation;
    const shouldLinkDriverToTransport = shouldCreateDriver && shouldCreateTransport && !!formData.linkDriverToTransport;
    const normalizedSlug = shouldEnableDriverAccess ? slugify(formData.slug) : '';
    const previousDriverId = editingTransport?.currentDriverId || '';
    const selectedExistingDriverRecord =
      shouldCreateDriver && formData.driverId
        ? drivers.find((driver) => driver.id === formData.driverId) || null
        : null;
    const selectedDriverPreviousTransportId =
      selectedExistingDriverRecord?.currentTransportId &&
      selectedExistingDriverRecord.currentTransportId !== editingTransport?.id
        ? selectedExistingDriverRecord.currentTransportId
        : '';

    if (shouldEnableDriverAccess) {
      const duplicateSlug = drivers.some((driver) => {
        if (formData.driverId && driver.id === formData.driverId) return false;
        return slugify(driver.accessSlug || driver.driverAccessSlug || '') === normalizedSlug;
      });

      if (duplicateSlug) {
        showMessage('Ya existe otro chofer con ese acceso operativo.', 'error');
        return;
      }
    }

    const transportsRef = collection(db, resolveTransportsCollectionPath(companyId));
    const driversRef = collection(db, resolveDriversCollectionPath(companyId));

    try {
      const { useAutomaticIdentityColor, ...persistableFormData } = formData;
      delete persistableFormData.includeDriver;
      delete persistableFormData.includeTransport;
      delete persistableFormData.enableDriverAccess;
      delete persistableFormData.enableLiquidation;
      delete persistableFormData.linkDriverToTransport;
      delete persistableFormData.driverStatus;
      delete persistableFormData.selectedExistingDriverId;
      delete persistableFormData.transportStatus;
      delete persistableFormData.liquidationTransportType;
      const transportIdentity = resolveTransportIdentity({
        ...editingTransport,
        ...persistableFormData,
        identityColor: formData.useAutomaticIdentityColor ? null : formData.identityColor,
        companyId,
        companyName,
        slug: normalizedSlug,
        id: editingTransport?.id
      });

      let driverId = shouldCreateDriver ? (formData.driverId || editingTransport?.currentDriverId || '') : '';
      const baseDriverStatus = shouldLinkDriverToTransport ? 'assigned' : (formData.driverStatus || 'available');
      const driverPayload = shouldCreateDriver
        ? {
            firstName: (formData.driverName || '').trim(),
            lastName: (formData.driverLastName || '').trim(),
            dni: (formData.driverDni || '').trim(),
            phone: (formData.phone || '').trim(),
            photoURL: (formData.photoURL || '').trim(),
            photoStoragePath: (formData.photoStoragePath || '').trim(),
            accessSlug: shouldEnableDriverAccess ? normalizedSlug : '',
            driverAccessSlug: shouldEnableDriverAccess ? normalizedSlug : '',
            password: shouldEnableDriverAccess ? (formData.password || '').trim() : '',
            companyId,
            companyName,
            status: baseDriverStatus,
            driverStatus: baseDriverStatus,
            isActive: baseDriverStatus !== 'inactive',
            preferredTransportType: formData.liquidationTransportType || formData.transportType || '',
            liquidationSettings: shouldEnableLiquidation
              ? {
                  enabled: true,
                  transportType: formData.liquidationTransportType || formData.transportType || '',
                  config: normalizeLiquidationPayload(formData.liquidation)
                }
              : {
                  enabled: false,
                  transportType: formData.liquidationTransportType || formData.transportType || '',
                  config: null
                },
            updatedAt: serverTimestamp()
          }
        : null;

      if (shouldCreateDriver && driverPayload) {
        if (driverId) {
          await setDoc(
            doc(driversRef, driverId),
            {
              ...driverPayload,
              createdAt: editingTransport?.driverCreatedAt || serverTimestamp()
            },
            { merge: true }
          );
        } else {
          const driverRef = await addDoc(driversRef, {
            ...driverPayload,
            createdAt: serverTimestamp()
          });
          driverId = driverRef.id;
        }
      }

      const driverSnapshot = buildDriverSnapshot(
        shouldCreateDriver
          ? {
              id: driverId,
              ...driverPayload
            }
          : {},
        {}
      );

      const transportPayload = shouldCreateTransport
        ? {
            ...persistableFormData,
            name: (formData.name || `Unidad ${isNew ? nextUnitNumber : editingTransport?.unitNumber || nextUnitNumber}`).trim(),
            transportModel: (formData.transportModel || '').trim(),
            vehicleLabel: (formData.transportModel || '').trim(),
            slug: shouldLinkDriverToTransport && shouldEnableDriverAccess ? normalizedSlug : '',
            driverAccessSlug: shouldLinkDriverToTransport && shouldEnableDriverAccess ? normalizedSlug : '',
            password: shouldLinkDriverToTransport && shouldEnableDriverAccess ? (formData.password || '').trim() : '',
            driverPassword: shouldLinkDriverToTransport && shouldEnableDriverAccess ? (formData.password || '').trim() : '',
            photoURL: shouldLinkDriverToTransport ? (formData.photoURL || '').trim() : '',
            photoStoragePath: shouldLinkDriverToTransport ? (formData.photoStoragePath || '').trim() : '',
            driverDni: shouldLinkDriverToTransport ? (formData.driverDni || '').trim() : '',
            currentDriverId: shouldLinkDriverToTransport ? driverId : '',
            currentDriverName: shouldLinkDriverToTransport ? driverSnapshot.driverName : '',
            currentDriverLastName: shouldLinkDriverToTransport ? driverSnapshot.driverLastName : '',
            currentDriverDni: shouldLinkDriverToTransport ? driverSnapshot.driverDni : '',
            currentDriverPhone: shouldLinkDriverToTransport ? driverSnapshot.driverPhone : '',
            currentDriverPhotoURL: shouldLinkDriverToTransport ? driverSnapshot.driverPhotoURL : '',
            currentDriverPhotoStoragePath: shouldLinkDriverToTransport ? (formData.photoStoragePath || '').trim() : '',
            driverName: shouldLinkDriverToTransport ? driverSnapshot.driverName : '',
            driverLastName: shouldLinkDriverToTransport ? driverSnapshot.driverLastName : '',
            phone: shouldLinkDriverToTransport ? driverSnapshot.driverPhone : '',
            liquidationEnabled: shouldEnableLiquidation && shouldLinkDriverToTransport,
            liquidation: shouldEnableLiquidation && shouldLinkDriverToTransport
              ? normalizeLiquidationPayload(formData.liquidation)
              : null,
            liquidationTransportType: shouldEnableLiquidation
              ? (formData.liquidationTransportType || formData.transportType || '')
              : '',
            companyId,
            companyName,
            unitNumber: isNew ? nextUnitNumber : editingTransport?.unitNumber || nextUnitNumber,
            identityColor: transportIdentity.identityColor,
            identitySoftColor: transportIdentity.identitySoftColor,
            transportStatus: formData.transportStatus || 'available',
            legacySourceId: editingTransport?._source === 'legacy'
              ? editingTransport.id
              : editingTransport?.legacySourceId || null,
            isActive: (formData.transportStatus || 'available') !== 'inactive',
            updatedAt: serverTimestamp()
          }
        : null;

      let transportId = editingTransport?.id || '';

      if (shouldCreateTransport && transportPayload) {
        if (isNew) {
          transportPayload.createdAt = serverTimestamp();
          const transportDoc = await addDoc(transportsRef, transportPayload);
          transportId = transportDoc.id;
        } else {
          transportPayload.createdAt = editingTransport?.createdAt || serverTimestamp();
          await setDoc(
            doc(transportsRef, editingTransport.id),
            transportPayload,
            { merge: true }
          );
          transportId = editingTransport.id;
        }

        const legacyTransportId =
          editingTransport?._source === 'legacy'
            ? editingTransport.id
            : editingTransport?.legacySourceId || transportId;

        if (legacyTransportId) {
          await setDoc(
            doc(db, `artifacts/${appId}/public/data/transports`, legacyTransportId),
            {
              ...transportPayload,
              legacySourceId: legacyTransportId,
              companyId,
              companyName,
              createdAt: transportPayload.createdAt || editingTransport?.createdAt || serverTimestamp(),
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );

          await setDoc(
            doc(transportsRef, transportId),
            {
              legacySourceId: legacyTransportId,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }
      }

      if (shouldLinkDriverToTransport && selectedDriverPreviousTransportId && selectedDriverPreviousTransportId !== transportId) {
        await setDoc(
          doc(transportsRef, selectedDriverPreviousTransportId),
          {
            currentDriverId: '',
            currentDriverName: '',
            currentDriverLastName: '',
            currentDriverDni: '',
            currentDriverPhone: '',
            currentDriverPhotoURL: '',
            driverName: '',
            driverLastName: '',
            driverDni: '',
            phone: '',
            photoURL: '',
            slug: '',
            driverAccessSlug: '',
            password: '',
            driverPassword: '',
            liquidationEnabled: false,
            liquidation: null,
            liquidationTransportType: '',
            transportStatus: 'available',
            updatedAt: serverTimestamp()
          },
          { merge: true }
        );
      }

      if (shouldCreateDriver && driverId) {
        await setDoc(
          doc(driversRef, driverId),
          {
            currentTransportId: shouldLinkDriverToTransport ? transportId : '',
            currentTransportName: shouldLinkDriverToTransport ? (transportPayload?.name || '') : '',
            currentTransportType: shouldLinkDriverToTransport ? (transportPayload?.transportType || formData.transportType || '') : '',
            currentTransportStatus: shouldLinkDriverToTransport ? (transportPayload?.transportStatus || formData.transportStatus || 'assigned') : 'available',
            driverStatus: shouldLinkDriverToTransport ? 'assigned' : (formData.driverStatus || 'available'),
            status: shouldLinkDriverToTransport ? 'assigned' : (formData.driverStatus || 'available'),
            isActive: (shouldLinkDriverToTransport ? 'assigned' : (formData.driverStatus || 'available')) !== 'inactive',
            updatedAt: serverTimestamp()
          },
          { merge: true }
        );
      }

      if (previousDriverId && previousDriverId !== (shouldLinkDriverToTransport ? driverId : '')) {
        await setDoc(
          doc(driversRef, previousDriverId),
          {
            currentTransportId: '',
            currentTransportName: '',
            currentTransportType: '',
            currentTransportStatus: 'available',
            driverStatus: 'available',
            status: 'available',
            updatedAt: serverTimestamp()
          },
          { merge: true }
        );
      }

      showMessage(
        getSaveSuccessMessage({
          isNewRecord: isNew,
          shouldCreateDriver,
          shouldCreateTransport,
          shouldLinkDriverToTransport,
          newDriverId: driverId,
          previousDriverId,
          previousLiquidationEnabled: Boolean(editingTransport?.liquidationEnabled || editingTransport?.liquidation?.enabled || editingTransport?.liquidationSettings?.enabled),
          currentLiquidationEnabled: Boolean(shouldEnableLiquidation && shouldLinkDriverToTransport),
          formData
        }),
        'success'
      );

      setEditingTransport(null);
    } catch (error) {
      console.error('Error al guardar el registro logístico:', error);
      showMessage('Error al guardar el registro logístico.', 'error');
    }
  };


  const handleDeleteTransportista = async (transportToDelete) => {
    if (!transportToDelete?.id) {
      showMessage('No se pudo identificar la ficha a eliminar.', 'error');
      return false;
    }

    try {
      const companyDocRef = doc(
        db,
        resolveTransportsCollectionPath(companyId),
        transportToDelete.id
      );

      await deleteDoc(companyDocRef);

      if (useLegacyFallback) {
        const legacyId =
          transportToDelete._source === 'legacy'
            ? transportToDelete.id
            : transportToDelete.legacySourceId;

        if (legacyId) {
          await deleteDoc(doc(db, `artifacts/${appId}/public/data/transports`, legacyId));
        }
      }

      if (expandedTransportId === transportToDelete.id) {
        setExpandedTransportId(null);
      }

      if (detailTransportId === transportToDelete.id) {
        setDetailTransportId(null);
      }

      setEditingTransport(null);
      showMessage('Ficha eliminada con éxito.', 'success');
      return true;
    } catch (error) {
      console.error('Error al eliminar la ficha del transporte:', error);
      showMessage('No se pudo eliminar la ficha.', 'error');
      return false;
    }
  };

  const handleOpenRouteSheet = (transportItem, entryOptions = {}) => {
    onSelectTransport({
      ...transportItem,
      companyId,
      companyName,
      companyModules,
      companyPlanSettings,
      commercialState,
      initialLogisticsView: entryOptions.initialLogisticsView || null,
      initialRouteEntry: entryOptions.initialRouteEntry || null,
      identityColor: transportItem.identityColor || resolveTransportIdentity(transportItem).identityColor,
      identitySoftColor: transportItem.identitySoftColor || resolveTransportIdentity(transportItem).identitySoftColor
    });
  };

  const transportTypes = TRANSPORT_TYPE_OPTIONS;

  const getCurrentOperationalRouteForTransport = (transport) => {
    if (!transport) return null;

    return (
      routes.find((route) => {
        const sameTransport =
          (route.transporterId && transport.id && route.transporterId === transport.id) ||
          (route.transporterSlug && transport.slug && route.transporterSlug === transport.slug) ||
          (!route.transporterId &&
            !route.transporterSlug &&
            (route.transport === transport.transportType ||
              route.transportType === transport.transportType));

        return sameTransport && getRouteStatus(route) === 'in_progress';
      }) || null
    );
  };

  const handleOpenTransportModule = (transport, moduleId) => {
    if (!transport) {
      showMessage('Primero necesitás una unidad para entrar a rutas.', 'info');
      return;
    }

    const transportCurrentRoute = getCurrentOperationalRouteForTransport(transport);

    if (moduleId === 'rutaActual') {
      if (!transportCurrentRoute) {
        showMessage('No hay una ruta activa para esta unidad.', 'info');
        return;
      }

      handleOpenRouteSheet(transport, {
        initialRouteEntry: {
          routeId: transportCurrentRoute.id,
          companyId
        }
      });
      return;
    }

    if (moduleId === 'rutaNueva') {
      handleOpenRouteSheet(transport, { initialLogisticsView: 'create' });
      return;
    }

    if (moduleId === 'historial') {
      handleOpenRouteSheet(transport, { initialLogisticsView: 'history' });
      return;
    }

    handleOpenRouteSheet(transport);
  };

  const buildTransportModuleActions = (transport) => {
    const transportCurrentRoute = getCurrentOperationalRouteForTransport(transport);

    return [
      {
        id: 'rutaActual',
        label: transportCurrentRoute ? 'Monitor activo' : 'Sin monitor',
        iconPath: 'M3.75 3v11.25A2.25 2.25 0 006 16.5h12A2.25 2.25 0 0020.25 14.25V3M3.75 21h16.5M7.5 6.75h9m-9 3.75h6',
        accent: transportCurrentRoute ? '#059669' : '#0ea5e9',
        primary: true,
        disabled: !transportCurrentRoute,
        onClick: () => handleOpenTransportModule(transport, 'rutaActual')
      },
      {
        id: 'rutaNueva',
        label: 'Nueva ruta',
        iconPath: 'M12 4.5v15m7.5-7.5h-15',
        accent: '#2563eb',
        onClick: () => handleOpenTransportModule(transport, 'rutaNueva')
      },
      {
        id: 'historial',
        label: 'Historial',
        iconPath: 'M8.25 6.75h7.5m-7.5 4.5h7.5m-7.5 4.5h4.5M6.75 3v2.25M17.25 3v2.25M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V7.5a2.25 2.25 0 00-2.25-2.25H3.75A2.25 2.25 0 001.5 7.5v11.25A2.25 2.25 0 003.75 21z',
        accent: '#7c3aed',
        onClick: () => handleOpenTransportModule(transport, 'historial')
      }
    ];
  };

  return (
    <div className="p-4 md:p-8 bg-slate-100 min-h-full">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Icon
            path="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0011.667 0l3.181-3.183m-4.991-2.691v4.992h4.992"
            className="w-12 h-12 text-blue-500 animate-spin"
          />
        </div>
      ) : (
        <>
          <div className="space-y-5">
            <style>{`
              @keyframes driversCardShine {
                0% { transform: translateX(-180%) skewX(-18deg); opacity: 0; }
                10% { opacity: 0; }
                22% { opacity: 0.18; }
                34% { opacity: 0.55; }
                48% { transform: translateX(240%) skewX(-18deg); opacity: 0; }
                100% { transform: translateX(240%) skewX(-18deg); opacity: 0; }
              }
            `}</style>
            {showDriversBoard ? (
              <DriversCommandBoard
                sectionRef={driversSectionRef}
                drivers={drivers}
                driverCards={driverModuleData.driverCards}
                selectedDriverId={selectedDriverId}
                selectedDriverReport={driverModuleData.selectedDriverReport}
                expandedDriverCardId={expandedDriverCardId}
                onSelectDriver={setSelectedDriverId}
                onToggleDriverCard={handleToggleDriverCard}
                onOpenTransport={handleEditTransportista}
                onOpenRouteSheet={handleOpenDriverRouteSheet}
                onOpenRouteMonitor={handleOpenDriverRouteMonitor}
                onCopyAccess={handleCopyDriverAccess}
                onOpenNewRecord={() => {
                  setEditingTransport(null);
                  setShowModal(true);
                }}
                onClose={() => setShowDriversBoard(false)}
              />
            ) : (
              <>
            <section id="logistics-hero" className="relative overflow-hidden rounded-[34px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#eef4ff_100%)] p-5 shadow-[0_24px_56px_rgba(15,23,42,0.10)]">
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.10),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(15,118,110,0.08),transparent_28%)]" />
              <div className="relative z-10 flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <button
                      type="button"
                      onClick={onBackToMenu}
                      className="inline-flex min-w-[10.5rem] items-center justify-center gap-2 rounded-2xl border border-slate-800 bg-[linear-gradient(180deg,#1e293b,#0f172a)] px-4 py-2 text-sm font-bold text-white shadow-[0_16px_30px_rgba(15,23,42,0.18)] transition hover:-translate-y-[1px] hover:bg-slate-800"
                    >
                      <Icon path="M9 15L3 9m0 0l6-6M3 12h12a6 6 0 010 12h-3" className="h-4 w-4" />
                      <span>Volver</span>
                    </button>
                    <span className="text-slate-300">|</span>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-slate-500">Centro operativo</p>
                  </div>
                  <h2 className="text-[30px] font-black tracking-[-0.04em] text-slate-900">Panel de Logística</h2>
                  <p className="text-sm text-slate-500">
                    Choferes, unidades y liquidación conviven ordenados desde un mismo centro operativo.
                  </p>
                  <button
                    type="button"
                    onClick={onStartTutorial}
                    className={`inline-flex items-center gap-2 rounded-2xl border border-cyan-300 bg-cyan-50 px-4 py-2 text-sm font-semibold text-cyan-800 transition-all hover:-translate-y-0.5 hover:bg-cyan-100 ${
                      tutorialSeen
                        ? ''
                        : 'animate-pulse shadow-[0_0_0_6px_rgba(34,211,238,0.18),0_0_24px_rgba(34,211,238,0.32)]'
                    }`}
                  >
                    <Icon path="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" className="h-4 w-4" />
                    Tutorial guiado
                  </button>
                </div>
              </div>
            </section>

            {transports.length > 0 ? (

              <section id="logistics-units-board" ref={unitsSectionRef} className="relative overflow-hidden rounded-[34px] border border-slate-200/80 bg-[linear-gradient(180deg,#f8fafc_0%,#eef2ff_100%)] shadow-[0_26px_60px_rgba(15,23,42,0.10)]">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white to-transparent" />
                <div className="relative space-y-5 p-4 md:p-5 xl:p-6 xl:pr-8 2xl:pr-10">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-slate-500">Tablero de unidades</p>
                      <h2 className="mt-1 text-xl font-bold tracking-[-0.03em] text-slate-900">Unidades de transporte</h2>
                      <p className="mt-2 max-w-2xl text-sm text-slate-500">
                          Cada unidad abre su propio circuito operativo: monitor, nueva hoja e historial sin mezclar capas.
                      </p>
                    </div>
                    <div className="grid min-w-[280px] grid-cols-1 gap-2 lg:w-[470px] lg:grid-cols-[1.65fr,1fr,1fr]">
                      <button
                        type="button"
                        onClick={handleToggleDriverBoard}
                        className={`group relative overflow-hidden rounded-[20px] border px-4 py-2 text-left text-white shadow-[0_22px_46px_rgba(14,165,233,0.28)] transition hover:-translate-y-0.5 hover:shadow-[0_28px_58px_rgba(14,165,233,0.34)] ${showDriversBoard ? 'border-cyan-100 bg-[linear-gradient(135deg,#082f49_0%,#0f766e_45%,#14b8a6_100%)]' : 'border-sky-200 bg-[linear-gradient(135deg,#0f172a_0%,#0369a1_45%,#06b6d4_100%)]'}`}
                      >
                        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.22),transparent_30%)] opacity-90" />
                        <div className="pointer-events-none absolute inset-y-0 left-[-24%] w-[34%] bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.05)_18%,rgba(255,255,255,0.72)_50%,rgba(255,255,255,0.06)_82%,transparent_100%)] opacity-80 blur-[1px]" style={{ animation: 'driversCardShine 5.8s linear infinite' }} />
                        <div className="relative z-10 flex items-center justify-between gap-4">
                          <div className="flex min-w-0 items-center gap-3">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[16px] bg-white/18 ring-1 ring-white/25 backdrop-blur-sm shadow-[0_12px_26px_rgba(15,23,42,0.18)]">
                              <Icon path="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.964 0a9 9 0 10-11.964 0m11.964 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9a3 3 0 11-6 0 3 3 0 016 0z" className="h-6 w-6" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-sky-100/90">Choferes</p>
                              <div className="mt-1 flex items-end gap-2">
                                <p className="text-[19px] font-black tracking-[-0.04em] leading-none">{drivers.length}</p>
                                <span className="truncate text-[11px] font-medium text-sky-50/90">Base operativa viva</span>
                              </div>
                            </div>
                          </div>
                          <div className="rounded-full bg-white/16 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-sky-50 shadow-[0_10px_22px_rgba(15,23,42,0.18)]">
                            {showDriversBoard ? 'Cerrar módulo' : 'Abrir módulo'}
                          </div>
                        </div>
                      </button>
                      <div className="rounded-[18px] border border-white/80 bg-white/78 px-3 py-2 shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
                        <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-500">Rutas vivas</p>
                        <div className="mt-1 flex items-center gap-2">
                          <span className="relative inline-flex h-2.5 w-2.5">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                          </span>
                          <span className="text-[15px] font-black tracking-[-0.03em] text-slate-900">{activeRoutesCount}</span>
                        </div>
                      </div>
                      <div className="rounded-[18px] border border-white/80 bg-white/78 px-3 py-2 shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
                        <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-500">Unidades</p>
                        <div className="mt-1 flex items-center gap-2">
                          <span className="inline-flex h-2.5 w-2.5 rounded-full bg-slate-400" />
                          <span className="text-[15px] font-black tracking-[0.03em] text-slate-900">{activeUnitsCount}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setEditingTransport(null);
                      setShowModal(true);
                    }}
                    className="relative flex w-full items-center justify-between gap-4 overflow-hidden rounded-[30px] border border-dashed border-blue-300/80 bg-[linear-gradient(135deg,#2563eb_0%,#0f766e_100%)] p-5 text-left shadow-[0_24px_50px_rgba(37,99,235,0.20)] transition-all duration-300 group hover:-translate-y-0.5 hover:shadow-[0_32px_60px_rgba(15,23,42,0.22)]"
                  >
                    <div
                      id="logistics-new-unit"
                      className="relative z-10 flex w-full items-center justify-between gap-4"
                    >
                      <div className="flex min-w-0 items-center gap-4 text-white">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[22px] bg-white/18 ring-1 ring-white/20 backdrop-blur-sm">
                          <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-7 w-7" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-100/90">Nuevo registro</p>
                          <p className="mt-1 text-lg font-bold">Abrir nuevo registro operativo</p>
                          <p className="mt-1 max-w-2xl text-sm text-blue-100/90">
                            Cargá chofer, transporte y liquidación en un mismo flujo, juntos o por separado.
                          </p>
                        </div>
                      </div>
                      <div className="inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-4 py-2 text-[11px] font-bold text-blue-700 shadow-lg shadow-black/10">
                        <span>Crear registro</span>
                        <Icon path="M13.5 4.5l6 6m0 0l-6 6m6-6h-15" className="h-3.5 w-3.5" />
                      </div>
                    </div>
                    <div className="absolute inset-0 h-full w-full bg-blue-500 opacity-20 transition-opacity duration-300 group-hover:opacity-35"></div>
                  </button>

                  <div className="columns-1 gap-3 md:columns-2 2xl:columns-3">
                    {transports.map((transport) => {
                      const pricingInfo = getTransportPricingInfo(transport, companyLiquidationSettings);
                      const tutorialIds =
                        transport === transports[0]
                          ? {
                              card: 'logistics-first-unit',
                              header: 'logistics-first-unit-header',
                              detail: 'logistics-first-unit-detail',
                              create: 'logistics-first-unit-create',
                              access: 'logistics-first-unit-access',
                              active: 'logistics-first-unit-active',
                              history: 'logistics-first-unit-history'
                            }
                          : null;

                      return (
                        <div
                          key={`${transport._source || 'company'}-${transport.id}`}
                          className="mb-3 break-inside-avoid"
                        >
                          <TransportCard
                            transport={transport}
                            contractClients={contractClientsByTransportId.get(String(transport?.id || '').trim()) || []}
                            onToggleUnit={handleToggleUnit}
                            onOpenDetail={handleToggleDetail}
                            onOpenRouteSheet={handleOpenRouteSheet}
                            isUnitOpen={expandedTransportId === '__all__' || expandedTransportId === transport.id}
                            isDetailOpen={detailTransportId === transport.id}
                            routeCount={countRoutes(transport)}
                            onEdit={handleEditTransportista}
                            pricingInfo={pricingInfo}
                            recentActions={recentActionsByTransportId.get(String(transport?.id || '').trim()) || []}
                            moduleActions={buildTransportModuleActions(transport)}
                            tutorialIds={tutorialIds}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
            ) : (
              <div className="space-y-5">
                <div className="rounded-[30px] border border-dashed border-slate-300 bg-white p-8 text-center shadow-sm">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-slate-100">
                    <Icon path="M12 4.5v15m7.5-7.5h-15" className="w-8 h-8 text-slate-500" />
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-slate-800">Todavía no hay unidades cargadas</h3>
                  <p className="mt-2 max-w-2xl mx-auto text-sm text-slate-500">
                    Arrancá creando chofer, transporte o ambos. La base operativa queda lista aunque todavía no estén vinculados.
                  </p>
                  {drivers.length > 0 ? (
                    <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700">
                      <span className="inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                      {drivers.length} chofer{drivers.length === 1 ? '' : 'es'} ya cargado{drivers.length === 1 ? '' : 's'}
                    </div>
                  ) : null}
                  <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingTransport(null);
                        setShowModal(true);
                      }}
                      className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-[16px] bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm"
                    >
                      <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
                      <span>Crear primer registro</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (showDemoUnit) {
                          setShowDemoUnit(false);
                          return;
                        }
                        setShowDemoUnit(true);
                        setDemoExpanded(true);
                      }}
                      className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-[16px] border border-cyan-200 bg-cyan-50 px-5 text-sm font-semibold text-cyan-800 shadow-sm transition hover:bg-cyan-100"
                    >
                      <Icon path="M13.5 4.5l6 6m0 0l-6 6m6-6h-15" className="h-4 w-4" />
                      <span>{showDemoUnit ? 'Ocultar ejemplo' : 'Ver ejemplo'}</span>
                    </button>
                  </div>
                </div>

                {showDemoUnit ? (
                  <div className="rounded-[30px] border border-cyan-200/80 bg-[linear-gradient(180deg,#f8fdff_0%,#eef8ff_100%)] p-4 shadow-[0_20px_48px_rgba(14,165,233,0.12)]">
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-cyan-700">Ejemplo guiado</p>
                        <h3 className="mt-1 text-lg font-bold tracking-[-0.03em] text-slate-900">Así se ve una unidad completa</h3>
                        <p className="mt-1 text-sm text-slate-600">
                          Es una referencia visual. No se guarda ni afecta la operación real.
                        </p>
                      </div>
                      <span className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700">
                        Demo
                      </span>
                    </div>

                    <TransportCard
                      transport={DEMO_TRANSPORT}
                      onToggleUnit={() => setDemoExpanded((current) => !current)}
                      onOpenDetail={() => setDemoDetailOpen((current) => !current)}
                      onOpenRouteSheet={() =>
                        showMessage?.('Esta unidad es solo un ejemplo visual. Creá una real para empezar a operar.', 'info')
                      }
                      routeCount={0}
                      pricingInfo={{
                        badge: 'Demo',
                        detail: 'Vista de referencia',
                        subdetail: 'Monitor, ruta e historial se habilitan cuando creás la unidad real.'
                      }}
                      isUnitOpen={demoExpanded}
                      isDetailOpen={demoDetailOpen}
                      tutorialIds={{
                        card: 'logistics-first-unit',
                        header: 'logistics-first-unit-header',
                        detail: 'logistics-first-unit-detail',
                        create: 'logistics-first-unit-create',
                        access: 'logistics-first-unit-access',
                        active: 'logistics-first-unit-active',
                        history: 'logistics-first-unit-history'
                      }}
                      onEdit={() =>
                        showMessage?.('Este ejemplo es solo para guía. La edición real empieza creando tu primera unidad.', 'info')
                      }
                      moduleActions={[
                        {
                          id: 'rutaActual',
                          label: 'Ruta activa',
                          onClick: () => showMessage?.('Cuando exista una ruta viva, desde acá vas a entrar al monitor individual.', 'info'),
                          disabled: false,
                          accent: '#0ea5e9'
                        },
                        {
                          id: 'rutaNueva',
                          label: 'Nueva ruta',
                          onClick: () => {
                            setEditingTransport(null);
                            setShowModal(true);
                          },
                          disabled: false,
                          primary: true,
                          accent: '#2563eb'
                        },
                        {
                          id: 'historial',
                          label: 'Historial',
                          onClick: () => showMessage?.('El historial aparece cuando la unidad ya tiene recorridos cargados.', 'info'),
                          disabled: false,
                          accent: '#8b5cf6'
                        }
                      ]}
                    />
                  </div>
                ) : null}
              </div>
            )}
              </>
            )}
          </div>
        </>
      )}

      <TransportistaModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingTransport(null);
        }}
        onSave={handleSaveTransportista}
        onDelete={handleDeleteTransportista}
        editingTransport={editingTransport}
        showMessage={showMessage}
        drivers={drivers}
        transportTypes={transportTypes}
        companyLiquidationSettings={companyLiquidationSettings}
        reportsEnabled={reportsEnabled}
        planLabel={planMeta?.label || 'un plan superior'}
        suggestedAlias={suggestedUnitAlias}
        companyId={companyId}
        usedIdentityColors={transports
          .filter((transport) => !editingTransport || transport.id !== editingTransport.id)
          .map((transport) => transport.identityColor)
          .filter(Boolean)}
        onStartTutorial={onStartUnitModalTutorial}
        tutorialSeen={unitModalTutorialSeen}
        tutorialRepeatEnabled={unitModalTutorialRepeatEnabled}
        onToggleTutorialRepeat={onToggleUnitModalTutorialRepeat}
      />
    </div>
  );
};

export default LogisticsDashboard;
